/**
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║                              ⛔ SEALED FILE ⛔                               ║
 * ╠══════════════════════════════════════════════════════════════════════════════╣
 * ║  This file is SEALED at 2,150 lines. DO NOT ADD more code here.             ║
 * ║                                                                              ║
 * ║  RULE: Extract new functionality to separate files:                         ║
 * ║    • Hooks      → src/hooks/                                                 ║
 * ║    • Utils      → src/utils/                                                 ║
 * ║    • Types      → src/types/                                                 ║
 * ║    • Components → src/components/GestureViewer/                              ║
 * ║                                                                              ║
 * ║  See existing extractions below for patterns to follow.                     ║
 * ║  If you MUST modify this file, you must REMOVE equivalent lines first.      ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 */

import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { getFrameImagePath, getFrameHdImagePath, getFrameUltraImagePath } from '@services/colmapLoader'
import { TapIndicatorState, NewModeViewerProps } from '@/types/TapIndicatorTypes'

import { useAppStore } from '@store/appStore'
import { useNavigationSettingsStore } from '@store/navigationSettingsSlice'
import { useNewModeGestures } from '@hooks/useNewModeGestures'
import { useNavigationSettings } from '@hooks/useNavigationSettings'
import { useFrameSelection } from '@hooks/useFrameSelection'
import { useCameraDiscovery } from '@hooks/useCameraDiscovery'
import { useConnectedFrameIndices, useContextFrameIndices, useStableOffsetMap } from '@hooks/useConnectedFrames'
import { useImageLoader } from '@hooks/useImageLoader'
import { useWorldUnderRedDot } from '@hooks/useViewportCalculations'
import { calculateCameraSpaceDepth, calculateFocalLength } from '@utils/ThumbnailScaleCalculator'
import { buildOrbitSequence } from '@utils/orbitNavigation'
import { useInitialViewportSetup, useViewportResize } from '@hooks/useViewportSetup'
import { getAnimationConfig, calculateFrameSequenceTiming } from '@services/../config/animationConfig'
import { getShopCardPreviewAnimationService, getDoubleClickAnimationService } from '@services/frameAnimationService'
import { SimpleZoomPan } from '../SimpleZoomPan'
import { CandidateFrameList } from './CandidateFrameList'
import { FrameRelationshipBalloon } from './FrameRelationshipBalloon'
import { NavigationSettingsBalloon } from './NavigationSettingsBalloon'
import { NavigationHintOverlay } from './NavigationHintOverlay'
import { MinimapOverlay } from './MinimapOverlay'
import { CameraTrajectoryOverlay } from '../CameraTrajectoryOverlay'
import { getCachedFrameDisplacement, clearDisplacementCache } from '@utils/framePathFinding'
import { calculateCameraVisibility } from '@utils/cameraProjection'
import { getRankedNearbyCamerasByImagePixels } from '@utils/CameraRankingUtils'
import { TapCameraIndicator } from './TapCameraIndicator'
import { calculateIndicatorPosition } from './calculateIndicatorPosition'
import { CameraThumbnailList } from './CameraThumbnailList'
import { SharedItemIndicator } from '@/features/shared-item-indicator'
import { useMarkItem } from '@hooks/useMarkItem'
import { ItemListPanel } from '@/features/delivery/components/ItemListPanel'
import { CartDrawer } from '@/features/delivery/components/CartDrawer'
import { useCartStore } from '@/stores/cartStore'
import { FrameItemMarkers } from './FrameItemMarkers'
import { WishlistLegend } from './WishlistLegend'
import { ChatBalloon } from './ChatBalloon'
import { ChatQuestionMarker } from './ChatQuestionMarker'
import { FrameContextMenu } from './FrameContextMenu'
import { useAuthStore } from '@/stores/authStore'
import { apiUrl } from '@/config'

// Preview mode state for shared URL tap indicator
// When a WhatsApp URL is opened, the indicator starts in preview mode (blue circle only)
// and switches to full mode on user tap

// Extracted to separate files:
// - @utils/candidateFiltering.ts (getFilteredCandidates, getOffsetMapPairs)
// - @/types/TapIndicatorTypes.ts (TapIndicatorState, NewModeViewerProps)
// - @hooks/useConnectedFrames.ts (useConnectedFrameIndices, useContextFrameIndices, useStableOffsetMap)
// - @hooks/useImageLoader.ts (useImageLoader)
// - @hooks/useViewportCalculations.ts (calculateViewportOffset, useWorldUnderRedDot, getEffectiveDisplayDimensions)
// - @hooks/useViewportSetup.ts (useInitialViewportSetup, useViewportResize)

/**
 * NEW MODE VIEWER - Simplified frame collage viewer
 * 
 * Features:
 * - Display all frames in world space
 * - Pan to navigate the collage
 * - Double-click frame to select it (blue border)
 * - Show connected frames
 * - Zoom only when frame is selected
 * 
 * No winner selection, no blur, no coverage calculations
 */
export function NewModeViewer({ shopId, showNavigationSettingsBalloon = false, setShowNavigationSettingsBalloon, isCurrentUserOwner = false }: NewModeViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const hdFailedSet = useRef<Set<string>>(new Set())
  const ultraFailedSet = useRef<Set<string>>(new Set())

  // Clear HD/ultra failed sets when shop/section changes, and periodically retry
  useEffect(() => {
    hdFailedSet.current.clear()
    ultraFailedSet.current.clear()
  }, [shopId])

  useEffect(() => {
    const interval = setInterval(() => {
      if (hdFailedSet.current.size > 0) {
        console.log(`[NewModeViewer] 🔄 Clearing HD failed set (${hdFailedSet.current.size} entries) for retry`)
        hdFailedSet.current.clear()
      }
      if (ultraFailedSet.current.size > 0) {
        console.log(`[NewModeViewer] 🔄 Clearing ultra failed set (${ultraFailedSet.current.size} entries) for retry`)
        ultraFailedSet.current.clear()
      }
    }, 60_000) // Retry every 60 seconds
    return () => clearInterval(interval)
  }, [])

  // imageLoaded state is now managed by useImageLoader hook (see below)
  const [hoveredCandidateFrameIndex, setHoveredCandidateFrameIndex] = useState<number | null>(null)
  const [selectedBalloonFrame, setSelectedBalloonFrame] = useState<number | null>(null)
  const [hoveredBalloonFrame, setHoveredBalloonFrame] = useState<number | null>(null)

  // ── Long-press context menu (owner-only rescan feature) ──
  const [contextMenu, setContextMenu] = useState<{
    open: boolean; x: number; y: number; frameIndex: number; frameFile: string
  } | null>(null)
  const longPressTimerRef = useRef<number | null>(null)

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])

  // Tap indicator state for two-step interaction
  /**
   * TapIndicatorState - Manages tap indicator for camera discovery
   * 
   * Coordinate Systems:
   * - screenPosition: Absolute viewport coordinates (for UI positioning)
   * - frameRelativeTapX/Y: Original tap position relative to frame (IMMUTABLE, for discovery)
   * - currentDisplayX/Y: Current projection position in frame (changes when navigating)
   */
  // TapIndicatorState interface moved to @/types/TapIndicatorTypes.ts

  const [tapIndicator, setTapIndicator] = useState<TapIndicatorState | null>(null)

  // Sync tap indicator point3D + discovery cameras to store for minimap overlay (Steps 11-12)
  useEffect(() => {
    const store = useAppStore.getState()
    if (tapIndicator?.point3D && tapIndicator.projections.size > 0) {
      store.setTapMinimapData(
        tapIndicator.point3D,
        Array.from(tapIndicator.projections.keys())
      )
    } else {
      store.setTapMinimapData(null, null)
    }
  }, [tapIndicator?.point3D, tapIndicator?.projections])

  // Auto-enable pan mode when tap indicator is active (for discovery navigation)
  useEffect(() => {
    const store = useAppStore.getState()
    if (tapIndicator?.point3D) {
      store.setPanModeEnabled(true)
    } else {
      store.setPanModeEnabled(false)
    }
  }, [tapIndicator?.point3D])

  // Get navigation settings for filtering
  const { settings: navigationSettings } = useNavigationSettings()

  // Sync panModeEnabled → navigationSettingsStore.enablePanSelection
  // (useFrameSelection reads from the store directly, not from props)
  const panModeEnabledForSync = useAppStore((state) => state.panModeEnabled)
  const arrowCandidateFrames = useAppStore((state) => state.arrowCandidateFrames)
  const orbitSequenceForRender = useAppStore((state) => state.orbitSequence)
  useEffect(() => {
    useNavigationSettingsStore.getState().updateSingleSetting('enablePanSelection', panModeEnabledForSync)
  }, [panModeEnabledForSync])

  // Camera discovery hook for thumbnail list
  const { state: discoveryState, showThumbnails, updateRadius, hideThumbnails, selectCamera } = useCameraDiscovery()
  
  // Playback trigger counter for badge click
  const [playbackTriggerCount, setPlaybackTriggerCount] = useState(0)

  // Delivery: mark item + cart hooks
  const { handleMarkItem, itemListOpen, setItemListOpen, cartDrawerOpen, setCartDrawerOpen, cartItemCount } = useMarkItem({ shopId })

  // Shared item indicator - parse URL parameters once at top level
  const [sharedItemParams, setSharedItemParams] = useState<{ x: number; y: number; r: number; targetFrame: number } | null>(null)

  // Preview mode: shared URL tap indicator shows only blue circle until user taps it
  const [tapIndicatorPreviewMode, setTapIndicatorPreviewMode] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const chatUserId = useAuthStore((s) => s.user?.id ?? null)

  // Push notification deep-link: auto-open ChatBalloon for a specific thread
  const [pushQuestionThreadId, setPushQuestionThreadId] = useState<string | null>(null)
  // Location marker data for the push-notification chat thread
  const [pushChatMarker, setPushChatMarker] = useState<{
    frameIndex: number
    imagePixelX?: number
    imagePixelY?: number
    point3D?: [number, number, number] | null
  } | null>(null)
  // Follow mode: auto-navigate to the customer's live frame
  const [followCustomer, setFollowCustomer] = useState(true)
  // Whether we've received a live location update from the customer
  const [pushChatIsLive, setPushChatIsLive] = useState(false)

  // Resolution tier badge: shown briefly when zoom crosses SD/HD/4K thresholds
  type ResolutionTierLabel = 'Standard' | 'HD' | '4K'
  const [resolutionBadge, setResolutionBadge] = useState<ResolutionTierLabel | null>(null)
  const resolutionBadgeTimerRef = useRef<number | null>(null)
  const prevResolutionTierRef = useRef<ResolutionTierLabel>('Standard')

  // Check for ?question= URL param on mount (from push notification click)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const questionId = params.get('question')
    if (questionId) {
      console.log('[NewModeViewer] Push notification deep-link: question=', questionId)
      setPushQuestionThreadId(questionId)
      // Clean the question param from URL to prevent re-opening on refresh
      params.delete('question')
      const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname
      window.history.replaceState({}, '', newUrl)
    }
  }, [])

  // Parse shared item URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const x = params.get('x')
    const y = params.get('y')
    const r = params.get('r')
    const frame = params.get('frame')

    if (x && y && frame) {
      const parsedX = parseFloat(x)
      const parsedY = parseFloat(y)
      const parsedR = r ? parseFloat(r) : 20
      const parsedFrame = parseInt(frame)

      if (!isNaN(parsedX) && !isNaN(parsedY) && !isNaN(parsedR) && !isNaN(parsedFrame)) {
        console.log('[NewModeViewer] Shared item params:', { x: parsedX, y: parsedY, r: parsedR, frame: parsedFrame })
        setSharedItemParams({ x: parsedX, y: parsedY, r: parsedR, targetFrame: parsedFrame })
      }
    }
  }, [])

  // Use centralized frame selection hook
  const { selectFrame } = useFrameSelection()

  // State from store
  const missingFrames = useAppStore((state) => state.missingFrames)
  const markFrameMissing = useAppStore((state) => state.markFrameMissing)
  const cameras = useAppStore((state) => state.cameras)
  const frameWorldPositions = useAppStore((state) => state.frameWorldPositions)
  const viewportWorldPosition = useAppStore((state) => state.viewportWorldPosition)
  const viewportSize = useAppStore((state) => state.viewportSize)
  const zoomScale = useAppStore((state) => state.zoomScale)
  const connectivityGraph = useAppStore((state) => state.connectivityGraph)
  const extendedConnectivityGraph = useAppStore((state) => state.extendedConnectivityGraph)
  const manuallySelectedFrameIndex = useAppStore((state) => state.manuallySelectedFrameIndex)
  const offsetMap = useAppStore((state) => state.offsetMap)
  const settings = useAppStore((state) => state.settings)
  const showDebugOverlay = useAppStore((state) => state.showDebugOverlay)
  const currentSectionFramesPath = useAppStore((state) => state.currentSectionFramesPath)
  const availableDatasets = useAppStore((state) => state.availableDatasets)
  const playback = useAppStore((state) => state.playback)
  const stopPlayback = useAppStore((state) => state.stopPlayback)
  const imageDimensions = useAppStore((state) => state.imageDimensions)
  const canUseTapIndicator = useAppStore((state) => state.canUseTapIndicator)
  const loadingProgress = useAppStore((state) => state.loadingProgress)
  const loadingStage = useAppStore((state) => state.loadingStage)

  // Get current camera from store instead of prop (to ensure it updates when currentCameraIndex changes)
  const currentCameraIndex = useAppStore((state) => state.currentCameraIndex)
  const currentCamera = cameras[currentCameraIndex]

  // Extract primitives from store selectors to avoid object reference changes triggering re-renders
  const viewportWidth = useAppStore((state) => state.viewportSize.width)
  const viewportHeight = useAppStore((state) => state.viewportSize.height)

  // 🎬 Get animation state from STORE (centralized in useFrameSelection)
  const storeAnimatingFrameIndex = useAppStore((state) => state.animatingFrameIndex)
  const storeAnimationTrigger = useAppStore((state) => state.animationTrigger)

  const isFrameSequenceAnimating = useAppStore((state) => state.isFrameSequenceAnimating)

  // Build orbit sequence when tap indicator has a 3D point with enough cameras
  useEffect(() => {
    const store = useAppStore.getState()
    if (tapIndicator?.point3D && tapIndicator.projections.size >= 3) {
      const tappedCamera = cameras[tapIndicator.frameIndex]
      const tappedDepth = tappedCamera
        ? calculateCameraSpaceDepth(tapIndicator.point3D, tappedCamera)
        : 1.0
      const sequence = buildOrbitSequence(
        tapIndicator.point3D,
        tapIndicator.projections,
        cameras,
        tapIndicator.tappedFrameIndex ?? tapIndicator.frameIndex,
        0     // 0 = no sector dedup → all in-bounds cameras sorted by angle
      )
      if (sequence.length >= 2) {
        // Attach projected pixel positions from the projections map
        for (const entry of sequence) {
          const pixel = tapIndicator.projections.get(entry.frameIndex)
          if (pixel) {
            entry.projectedPixel = pixel
          }
        }
        store.setOrbitSequence(sequence, tappedDepth, tapIndicator.point3D, tapIndicator.circleRadiusMeters)
        console.log(`🔄 [Orbit] Built sequence: ${sequence.length} cameras around 3D point`)
      } else {
        store.clearOrbit()
      }
    } else {
      store.clearOrbit()
    }
  }, [tapIndicator?.point3D, tapIndicator?.projections, cameras])

  // 🎯 Wrapper for camera selection that shows the red circle at projected pixel
  const handleCameraSelection = (cameraId: number, projectedPixel?: [number, number], radiusPxImageSpace?: number, scaleData?: { circleScale: number; distanceScale: number; circleRadiusPx: number; depth: number; focalLength: number }) => {
    console.log(`🎯 [handleCameraSelection] Called with:`, {
      cameraId,
      projectedPixel,
      radiusPxImageSpace,
      hasTapIndicator: !!tapIndicator,
      tapIndicatorData: tapIndicator ? { frameCamera: !!tapIndicator.frameCamera, frameIndex: tapIndicator.frameIndex, cameraCount: tapIndicator.cameraCount, projections: tapIndicator.projections?.size ?? 0 } : null
    })
    
    // Reset panned position so indicator will recalculate in new frame
    setTapIndicatorPannedPosition(null)
    
    // First, navigate to the new frame
    selectCamera(cameraId, projectedPixel)
    
    // Then, if we have a projected pixel, update tap indicator to show circle at that location
    if (projectedPixel && tapIndicator?.frameCamera && projectedPixel.length === 2) {
      console.log(`🎯 [handleCameraSelection] Has projected pixel, updating indicator...`)
      // Get the camera for the selected frame
      const selectedCamera = cameras[cameraId]
      console.log(`🎯 [handleCameraSelection] Selected camera:`, { exists: !!selectedCamera, width: selectedCamera?.intrinsics?.width, height: selectedCamera?.intrinsics?.height })
      
      if (selectedCamera) {
        // Convert pixel coordinates to screen coordinates
        // We need to find the selected frame element to get its viewport position
        const selectedFrameEl = document.querySelector('[data-selected-frame="true"]') as HTMLDivElement
        console.log(`🎯 [handleCameraSelection] Selected frame element:`, { exists: !!selectedFrameEl })
        
        if (selectedFrameEl) {
          const rect = selectedFrameEl.getBoundingClientRect()
          const containerRect = containerRef.current?.getBoundingClientRect()
          
          console.log(`🎯 [handleCameraSelection] Rects:`, { frameRect: { width: rect.width, height: rect.height }, containerRect: { width: containerRect?.width, height: containerRect?.height } })
          
          if (containerRect) {
            // Convert image pixels to display coordinates
            const displayX = (projectedPixel[0] / selectedCamera.intrinsics.width) * rect.width
            const displayY = (projectedPixel[1] / selectedCamera.intrinsics.height) * rect.height
            
            // Convert to screen coordinates
            const screenX = rect.left - containerRect.left + displayX
            const screenY = rect.top - containerRect.top + displayY
            
            console.log(`🎯 [Camera Selection] Showing circle at projected pixel: (${projectedPixel[0].toFixed(1)}, ${projectedPixel[1].toFixed(1)}) → display: (${displayX.toFixed(1)}, ${displayY.toFixed(1)}) → screen: (${screenX.toFixed(1)}, ${screenY.toFixed(1)})`)
            console.log(`🎯 [Camera Selection] Updating tap indicator for frame ${cameraId}:`, {
              newDisplayPosition: { x: displayX, y: displayY },
              originalFrameRelativeTap: {
                x: tapIndicator.frameRelativeTapX,
                y: tapIndicator.frameRelativeTapY
              },
              projectionsSize: tapIndicator.projections.size
            })
            
            // Update tap indicator to show circle at new location
            // CRITICAL: Keep original tap coords, projections, and point3D immutable!
            // Use incoming radius if provided (from thumbnail selection), otherwise preferred, otherwise fallback
            const preferredRadius = useAppStore.getState().preferredDiscoveryRadiusPx
            const finalRadius = radiusPxImageSpace ?? preferredRadius
            console.log(`📏 [handleCameraSelection] Radius decision: incoming=${radiusPxImageSpace?.toFixed(1)}, preferred=${preferredRadius?.toFixed(1)}, final=${finalRadius?.toFixed(1)}`)
            setTapIndicator({
              show: true,
              cameraCount: tapIndicator.cameraCount,
              screenPosition: { x: screenX, y: screenY },
              frameRelativeTapX: tapIndicator.frameRelativeTapX,  // IMMUTABLE: Keep original!
              frameRelativeTapY: tapIndicator.frameRelativeTapY,  // IMMUTABLE: Keep original!
              currentDisplayX: displayX,                          // UPDATE: New projection
              currentDisplayY: displayY,                          // UPDATE: New projection
              frameCamera: selectedCamera,
              frameIndex: cameraId,
              projections: tapIndicator.projections,              // IMMUTABLE: Keep original!
              point3D: tapIndicator.point3D,                      // IMMUTABLE: Keep original!
              radiusPxImageSpace: finalRadius,
              circleRadiusMeters: tapIndicator.circleRadiusMeters, // IMMUTABLE: Keep original 3D radius!
              scaleData: scaleData                                 // UPDATE: New per-camera scale data
            })
            console.log(`✅ [handleCameraSelection] Updated tapIndicator with new position`)
          }
        }
      }
    } else {
      console.log(`⚠️ [handleCameraSelection] Skipping indicator update:`, {
        hasProjectedPixel: !!projectedPixel,
        hasTapIndicator: !!tapIndicator,
        hasTapIndicatorFrameCamera: !!tapIndicator?.frameCamera,
        pixelLength: projectedPixel?.length
      })
    }
  }

  // 🔄 Orbit step: switch frame + update tap indicator position WITHOUT moving viewport
  const handleOrbitStep = useCallback((frameIndex: number, projectedPixel?: [number, number]) => {
    const store = useAppStore.getState()
    store.setManuallySelectedFrame(frameIndex)
    console.log(`[OrbitStep] → frame ${frameIndex}, projPixel=[${projectedPixel?.[0]?.toFixed(1)},${projectedPixel?.[1]?.toFixed(1)}]`)

    if (!projectedPixel || !tapIndicator) return
    const selectedCamera = cameras[frameIndex]
    if (!selectedCamera) return

    // Compute display coordinates directly from store state (no DOM query needed)
    // The selected frame renders at world position: worldX/Y, with its top-left at worldX - imgW/2
    const framePos = store.frameWorldPositions[frameIndex]
    if (!framePos) return
    const { zoomScale: zoom, viewportWorldPosition: vp, viewportSize: vs } = store
    const imgW = selectedCamera.intrinsics.width
    const imgH = selectedCamera.intrinsics.height

    // World position of the projected pixel
    const pixelWorldX = framePos.worldX + (projectedPixel[0] - imgW / 2)
    const pixelWorldY = framePos.worldY + (projectedPixel[1] - imgH / 2)

    // Convert world → screen (container-relative)
    const screenX = (pixelWorldX - vp.x) * zoom
    const screenY = (pixelWorldY - vp.y) * zoom

    // Display coordinates = screenX relative to frame's screen top-left
    const framePosScreenX = (framePos.worldX - imgW / 2 - vp.x) * zoom
    const framePosScreenY = (framePos.worldY - imgH / 2 - vp.y) * zoom
    const frameDisplayW = imgW * zoom
    const frameDisplayH = imgH * zoom
    const displayX = (projectedPixel[0] / imgW) * frameDisplayW
    const displayY = (projectedPixel[1] / imgH) * frameDisplayH

    console.log(`[OrbitStep] screenX=${screenX.toFixed(1)} screenY=${screenY.toFixed(1)} displayX=${displayX.toFixed(1)} displayY=${displayY.toFixed(1)}`)

    void framePosScreenX; void framePosScreenY; void vs

    setTapIndicator(prev => prev ? {
      ...prev,
      screenPosition: { x: screenX, y: screenY },
      currentDisplayX: displayX,
      currentDisplayY: displayY,
      frameCamera: selectedCamera,
      frameIndex,
    } : prev)
  }, [cameras, tapIndicator])

  // 🎯 CRITICAL: Clear hover during animation to prevent z-index interference
  // When animation plays, hoveredCandidateFrameIndex should be suppressed to avoid
  // any frame jumping on top of the selected frame (which should always be at z-index 30000)
  useEffect(() => {
    if (isFrameSequenceAnimating) {
      setHoveredCandidateFrameIndex(null)
    }
  }, [isFrameSequenceAnimating])

  // 🎯 Overlay visibility flags from store (Feature 2: Unified Dismiss)
  const frameBalloonVisible = useAppStore((state) => state.frameBalloonVisible)
  
  // 🎯 Candidate frames for trajectory overlay (memoized to avoid infinite re-renders)
  const candidateFrameIndices = useAppStore((state) => state.candidateFrameIndices)
  const candidateFrameSet = useMemo(() => new Set(candidateFrameIndices), [candidateFrameIndices])

  // 🆕 Get animation state from gesture hook (same instance, synchronized updates)
  const { isPanningRef, animatingFrameIndex: hookAnimatingFrameIndex, animationTrigger: hookAnimationTrigger } = useNewModeGestures(containerRef, {
    sensitivity: 'medium',
    enablePan: true,
    enablePinch: true,
    imageDimensions: imageDimensions ? {
      displayWidth: imageDimensions.displayWidth,
      displayHeight: imageDimensions.displayHeight
    } : undefined,
    navigationSettings,
    onPanEnd: (timeMs) => {
      lastPanEndTimeRef.current = timeMs
      // Get current frame from store (not from closure)
      const currentFrame = useAppStore.getState().manuallySelectedFrameIndex
      panEndSelectedFrameRef.current = currentFrame
      console.log(`[Pan-End] Gesture hook notified, protecting frame ${currentFrame} for 2 seconds`)
    },
    onFrameShow: (frameIndex: number) => {
      // 🆕 Update animation frame via hook's handleFrameShow
      console.log(`🎬 [Visual] onFrameShow callback: bringing frame ${frameIndex} to top z-index`)
      // Animation frame index updated directly in hook
    },
    onOrbitStep: (frameIndex: number, projectedPixel?: [number, number]) => {
      // Update tap indicator after orbit step — use requestAnimationFrame so DOM has updated
      requestAnimationFrame(() => {
        handleCameraSelection(frameIndex, projectedPixel)
      })
    },
  })

  // 🎬 Combine animation state from store AND hook (store takes precedence)
  // The store animation is triggered by useFrameSelection for ALL selections with jump > 1
  // The hook animation is a fallback for pan-end specific animations (legacy)
  const animatingFrameIndex = storeAnimatingFrameIndex ?? hookAnimatingFrameIndex
  const animationTrigger = storeAnimationTrigger ?? hookAnimationTrigger

  // Get reconstruction method for this shop
  const reconstructionMethod = useMemo(() => {
    const dataset = availableDatasets.find(d => d.id === shopId)
    return (dataset as any)?.reconstructionMethod as 'colmap' | 'phase_correlation' | undefined
  }, [availableDatasets, shopId])

  // Stabilize offsetMap using extracted hook
  const offsetMapStable = useStableOffsetMap(offsetMap)

  // 🆕 Store persistent context frames that remain visible even when selected frame exits viewport
  const [persistentContextFrames, setPersistentContextFrames] = useState<Set<number>>(new Set())

  // Get image path and load
  const imagePath = currentCamera ? getFrameImagePath(shopId, currentCamera.frame_file, currentSectionFramesPath || undefined) : ''
  
  // Get warm-start frame
  const warmStartFrame = useAppStore((state) => state.warmStartFrame)

  // Get frame dimensions from camera intrinsics (fallback if imageDimensions not set)
  const cameraFrameWidth = currentCamera?.intrinsics?.width || 450
  const cameraFrameHeight = currentCamera?.intrinsics?.height || 800
  
  // Calculate display dimensions based on viewport height
  const displayHeightForFrames = showDebugOverlay ? window.innerHeight * 0.8 : window.innerHeight
  const frameAspectRatio = cameraFrameWidth / cameraFrameHeight
  const displayWidthForFrames = displayHeightForFrames * frameAspectRatio
  
  // Use image dimensions if loaded, otherwise use calculated dimensions
  const effectiveDisplayWidth = imageDimensions?.displayWidth && imageDimensions.displayWidth > 0 ? imageDimensions.displayWidth : displayWidthForFrames
  const effectiveDisplayHeight = imageDimensions?.displayHeight && imageDimensions.displayHeight > 0 ? imageDimensions.displayHeight : displayHeightForFrames

  // Sync store overlay visibility flags to component state (Feature 2: Unified Dismiss)
  useEffect(() => {
    if (!frameBalloonVisible && selectedBalloonFrame !== null) {
      setSelectedBalloonFrame(null);
      setHoveredBalloonFrame(null);
    }
  }, [frameBalloonVisible, selectedBalloonFrame]);

  // Update viewport size on mount and resize (extracted to useViewportSetup.ts)
  useViewportResize(containerRef, showDebugOverlay)

  // Load image using extracted hook
  const { imageLoaded, setImageLoaded } = useImageLoader({
    imagePath,
    showDebugOverlay,
    currentCamera,
    currentSectionFramesPath,
    cameras
  })

  // At initial load OR when dataset changes, zoom to fit frame in viewport and center on holy red dot
  // (extracted to useViewportSetup.ts)
  useInitialViewportSetup({
    imageDimensions,
    viewportSize,
    frameWorldPositions,
    displayHeightForFrames,
    shopId,
    currentSectionFramesPath
  })

  // Calculate context frame indices using extracted hook
  const contextFrameIndices = useContextFrameIndices(
    manuallySelectedFrameIndex,
    extendedConnectivityGraph,
    connectivityGraph
  )

  // Calculate connected frames using extracted hook
  const connectedFrameIndices = useConnectedFrameIndices({
    manuallySelectedFrameIndex,
    connectivityGraph,
    offsetMapStable,
    settings: { max3DDistance: settings.max3DDistance, maxDz: settings.maxDz },
    reconstructionMethod
  })

  // Calculate viewport offset for transform
  // Simplified: viewportWorldPosition represents pan offset from origin (0, 0)
  // Screen formula: screen = -viewportWorldPosition * zoom
  // (negative because panning right moves viewport left, revealing content to the right)
  const totalViewportOffset = {
    x: -viewportWorldPosition.x * zoomScale,
    y: -viewportWorldPosition.y * zoomScale
  }

  // Calculate what world coordinate is under the fixed red dot at screen center
  // World coordinates under viewport center (the "red dot")
  const worldUnderRedDot = useWorldUnderRedDot(viewportSize, totalViewportOffset, zoomScale)

  // Get frames to render: selected frame + connected frames + extra context frames
  // Use frameWorldPositions which are absolute canvas positions (never change)
  // Filter and sort to match candidate list ordering
  // 🎬 CRITICAL: During animation, render frames around the ANIMATING frame (current viewport center)
  // After animation, render frames around the SELECTED frame
  // This ensures consistent frame sets stay visible, preventing flicker
  const frameIndexForRendering = isFrameSequenceAnimating && animatingFrameIndex !== null 
    ? animatingFrameIndex 
    : (manuallySelectedFrameIndex ?? 0)

  // Track previous frame for smooth NSEW transitions (keep old frame in DOM during fade)
  const prevFrameRef = useRef<number | null>(null)
  useEffect(() => {
    prevFrameRef.current = frameIndexForRendering
  }, [frameIndexForRendering])

  const framesToRender = useMemo(() => {
    // When pan mode is OFF, only render current frame + arrow candidates (NSEW)
    if (!panModeEnabledForSync) {
      const arrowCandidates = arrowCandidateFrames
      const framesToShow = new Set<number>()
      framesToShow.add(frameIndexForRendering)
      // Include previous frame so it stays in DOM for opacity fade-out
      if (prevFrameRef.current !== null) {
        framesToShow.add(prevFrameRef.current)
      }
      for (const candidate of arrowCandidates) {
        framesToShow.add(candidate)
      }
      // Pre-load orbit chain frames so they're in the DOM before orbit steps (prevents flicker)
      if (orbitSequenceForRender) {
        for (const entry of orbitSequenceForRender) {
          framesToShow.add(entry.frameIndex)
        }
      }
      const result = frameWorldPositions.filter(fp => framesToShow.has(fp.frameIndex))
      // Sort: current frame first, then by distance
      return result.sort((a, b) => {
        if (a.frameIndex === frameIndexForRendering) return -1
        if (b.frameIndex === frameIndexForRendering) return 1
        return Math.abs(a.frameIndex - frameIndexForRendering) - Math.abs(b.frameIndex - frameIndexForRendering)
      })
    }

    // Pan mode ON: use full rendering pipeline
    // Show frames that are directly connected to selected frame (for rendering)
    // This is separate from navigationSettings filtering which affects candidate list
    const connectedSet = connectivityGraph?.get(frameIndexForRendering) || new Set()
    
    // IMPORTANT: First apply universal window filtering
    // - For phase correlation: use sequentialWindow (±5 frames)
    // - For COLMAP or unknown: use contextualWindow (±15 frames)
    let candidateFrames = frameWorldPositions
    if (frameIndexForRendering !== null) {
      // 🔧 FIX: Use contextualWindow for rendering (all reconstruction methods)
      // sequentialWindow is only for jump distance enforcement during panning
      const maxDistance = navigationSettings?.contextualWindow ?? 15
      
      candidateFrames = frameWorldPositions.filter(framePos => {
        const frameDiff = Math.abs(framePos.frameIndex - frameIndexForRendering)
        return frameDiff <= maxDistance
      })
    }
    
    // Then apply connectivity filtering
    // CRITICAL: Always include sequential neighbors (±5) to enable smooth navigation
    const sequentialWindowSize = navigationSettings?.sequentialWindow ?? 5
    const filtered = candidateFrames.filter(framePos => {
      // Always include the selected frame
      if (framePos.frameIndex === frameIndexForRendering) {
        return true
      }
      
      // ALWAYS include frames within sequential window (nearby sequential frames)
      // This is MANDATORY for smooth navigation through sequential frames
      const frameDiff = Math.abs(framePos.frameIndex - frameIndexForRendering)
      if (frameDiff <= sequentialWindowSize) {
        return true
      }
      
      // Include directly connected frames (extended pairs)
      if (connectedSet.has(framePos.frameIndex)) {
        return true
      }
      
      // FALLBACK: If no connectivity data, include all nearby frames (±10)
      // This handles cases where connectivity graph is empty
      if (connectedSet.size === 0 && frameDiff <= 10) {
        return true
      }
      
      return false
    })

    // Define existing frame indices for filtering context frames
    const existingFrameIndices = new Set(filtered.map(f => f.frameIndex))

    // Add extra context frames when enabled
    // Note: Removed viewport width restriction to support mobile devices
    let contextFrames: typeof frameWorldPositions = []
    if (navigationSettings.showExtraContextFrames && frameIndexForRendering !== null) {
      // Get frames from extended connectivity graph (all reachable frames)
      // Exclude those already in filtered list and the selected frame itself
      const directlyConnected = connectivityGraph?.get(frameIndexForRendering) || new Set()
      const extendedReachable = extendedConnectivityGraph?.get(frameIndexForRendering) || new Set()
      
      // Context frames are those in extended graph but NOT in direct connection
      const contextFrameIndicesSet = new Set<number>()
      // 🔧 FIX: Use contextualWindow for context frames (all reconstruction methods)
      // sequentialWindow is only for jump distance enforcement during panning
      const contextWindowSize = navigationSettings?.contextualWindow ?? 15
      
      for (const frameIdx of extendedReachable) {
        // Skip if already in filtered list
        if (existingFrameIndices.has(frameIdx)) continue
        // Skip if directly connected (those are in filtered list anyway)
        if (directlyConnected.has(frameIdx)) continue
        // Skip the selected frame
        if (frameIdx === frameIndexForRendering) continue
        // ENFORCE WINDOW LIMIT: Skip frames beyond contextual window
        if (Math.abs(frameIdx - frameIndexForRendering) > contextWindowSize) continue
        
        contextFrameIndicesSet.add(frameIdx)
      }
      
      // Convert to frame positions
      contextFrames = frameWorldPositions.filter(fp => contextFrameIndicesSet.has(fp.frameIndex))
    }

    // 🆕 Include persistent context frames in rendering (frames that remain visible even when selected frame exits viewport)
    // But exclude frames already in current contextFrames to prevent duplicates
    // Only include persistent context frames when showExtraContextFrames is enabled
    const currentContextFrameIndices = new Set(contextFrames.map(f => f.frameIndex))
    // 🔧 FIX: Use contextualWindow for persistent frames (all reconstruction methods)
    // sequentialWindow is only for jump distance enforcement during panning
    const persistentWindowSize = navigationSettings?.contextualWindow ?? 15
    const persistentContextFramePositions = navigationSettings.showExtraContextFrames && frameIndexForRendering !== null ? frameWorldPositions.filter(framePos => 
      persistentContextFrames.has(framePos.frameIndex) && 
      framePos.frameIndex !== frameIndexForRendering && // Don't duplicate selected frame
      !existingFrameIndices.has(framePos.frameIndex) && // Don't duplicate frames already in filtered list
      !currentContextFrameIndices.has(framePos.frameIndex) && // Don't duplicate frames already in current context
      Math.abs(framePos.frameIndex - frameIndexForRendering) <= persistentWindowSize // ENFORCE WINDOW LIMIT
    ) : []
    
    // Combine filtered, current context, and persistent context frames
    // Use a Map to deduplicate by frameIndex
    const allFramesMap = new Map<number, typeof frameWorldPositions[0]>()
    
    // Add filtered frames first (highest priority)
    filtered.forEach(frame => allFramesMap.set(frame.frameIndex, frame))
    
    // Add context frames (will override filtered if same frame, but shouldn't happen)
    contextFrames.forEach(frame => allFramesMap.set(frame.frameIndex, frame))
    
    // Add persistent context frames (lowest priority)
    persistentContextFramePositions.forEach(frame => allFramesMap.set(frame.frameIndex, frame))
    
    const allFrames = Array.from(allFramesMap.values())

    // Track context frame indices (for sorting and rendering) - includes both current and persistent
    // Don't call setState here! Just compute locally
    const combinedContextIndices = new Set<number>([
      ...contextFrames.map(f => f.frameIndex),
      ...persistentContextFrames
    ])

    // Then, sort using same logic as candidate list
    if (manuallySelectedFrameIndex === null) {
      return allFrames
    }

    // Sort by relationship metrics (same as CandidateFrameList)
    const getSortMetrics = (frameIndex: number) => {
      const metrics = {
        distance3d: Infinity,
        indexDifference: Infinity,
      }

      const fromCam = cameras[frameIndexForRendering]
      const toCam = cameras[frameIndex]
      
      if (fromCam?.position && toCam?.position) {
        const [x1, y1, z1] = fromCam.position
        const [x2, y2, z2] = toCam.position
        metrics.distance3d = Math.sqrt(
          (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2
        )
      }

      metrics.indexDifference = Math.abs(frameIndex - frameIndexForRendering)

      return metrics
    }

    const isConnected = (frameIndex: number) => connectedFrameIndices.has(frameIndex)
    const isContextFrame = (frameIndex: number) => combinedContextIndices.has(frameIndex)

    const sorted = [...allFrames].sort((a, b) => {
      // Primary: Selected frame always goes first
      if (a.frameIndex === frameIndexForRendering && b.frameIndex !== frameIndexForRendering) return -1
      if (a.frameIndex !== frameIndexForRendering && b.frameIndex === frameIndexForRendering) return 1
      
      // Secondary: Filtered frames before context frames
      const aIsContext = isContextFrame(a.frameIndex)
      const bIsContext = isContextFrame(b.frameIndex)
      if (!aIsContext && bIsContext) return -1
      if (aIsContext && !bIsContext) return 1
      
      // Both are same type: sort by connected + distance
      const metricsA = getSortMetrics(a.frameIndex)
      const metricsB = getSortMetrics(b.frameIndex)

      const aConnected = isConnected(a.frameIndex) ? 0 : 1
      const bConnected = isConnected(b.frameIndex) ? 0 : 1
      
      if (aConnected !== bConnected) {
        return aConnected - bConnected
      }

      // For connected frames: if distance < 1.0, prioritize continuity over 3D distance
      if (aConnected === 0) { // Both are connected
        const aCloseDistance = metricsA.distance3d < 1.0
        const bCloseDistance = metricsB.distance3d < 1.0
        
        // If both have close distance, sort by index proximity (continuity)
        if (aCloseDistance && bCloseDistance) {
          const indexDiff = metricsA.indexDifference - metricsB.indexDifference
          if (indexDiff !== 0) return indexDiff
        }
        
        // If only one has close distance, it goes first
        if (aCloseDistance !== bCloseDistance) {
          return aCloseDistance ? -1 : 1
        }
      }

      // Secondary sort: by 3D distance (ascending - closest first)
      return metricsA.distance3d - metricsB.distance3d
    })
    
    return sorted
  }, [
    frameWorldPositions,  // Stable array
    frameIndexForRendering,  // During animation, updates based on animatingFrameIndex
    connectedFrameIndices,  // Set from useMemo
    // Remove cameras, offsetMap, connectivityGraph, extendedConnectivityGraph from deps - capture from closure
    // Instead, use the stable refs above if needed, but since they don't change during pan, capture them
    settings.max3DDistance, settings.maxDz,  // Primitives
    navigationSettings.showExtraContextFrames, navigationSettings.sequentialWindow, navigationSettings.contextualWindow,  // Primitives
    viewportWidth, viewportHeight,  // Extracted primitives
    imageDimensions?.displayWidth, imageDimensions?.displayHeight,  // Extracted primitives
    panModeEnabledForSync, arrowCandidateFrames, orbitSequenceForRender,  // Pan mode optimization
  ])

  // 🆕 Create candidates that match framesToRender: selected + connected + context frames
  const allCandidates = useMemo(() => {
    // Get the same frame indices that are shown in framesToRender
    const framesToRenderIndices = new Set(framesToRender.map(f => f.frameIndex))
    
    // Get connected frame indices for styling (based on rendering frame, not animated frame)
    const connectedSet = connectivityGraph?.get(frameIndexForRendering) || new Set()
    
    // 🎬 CRITICAL FIX: During animation, highlight the currently animating frame
    // During animation, isFrameSequenceAnimating=true and animatingFrameIndex changes each frame (0→99)
    // This ensures the animating frame always has z-index 30000 (on top)
    // After animation, revert to manuallySelectedFrameIndex for normal frame selection
    const frameToHighlight = isFrameSequenceAnimating && animatingFrameIndex !== null 
      ? animatingFrameIndex 
      : (manuallySelectedFrameIndex ?? 0)
    
    const result = cameras
      .map((camera, idx) => {
        if (!camera || !framesToRenderIndices.has(idx)) {
          return null // Skip frames not in framesToRender
        }
        
        const isWinner = idx === frameToHighlight
        const isConnected = connectedSet.has(idx)
        const isContextFrame = idx !== frameIndexForRendering && !isConnected
        
        return {
          camera: camera,
          frameIndex: idx,
          imagePath: getFrameImagePath(shopId, camera?.frame_file || '', currentSectionFramesPath || undefined),
          isWinner,
          passedFilters: isConnected, // Connected frames "passed filters"
          isContextFrame,
        }
      })
      .filter(item => item !== null)
    
    return result
  }, [cameras, frameIndexForRendering, animatingFrameIndex, isFrameSequenceAnimating, manuallySelectedFrameIndex, shopId, framesToRender, connectivityGraph, currentSectionFramesPath])

  // Track last auto-selected frame to prevent infinite loops
  const lastAutoSelectedRef = useRef<number | null>(null)

  // Track last auto-select time to throttle frequent calls
  const lastAutoSelectTimeRef = useRef(0)
  const lastPanEndTimeRef = useRef(0)  // Track when pan-end just happened
  const panEndSelectedFrameRef = useRef<number | null>(null)  // Track which frame pan-end selected

  // Clear displacement cache when selected frame changes to ensure fresh position calculations
  useEffect(() => {
    if (manuallySelectedFrameIndex !== null) {
      clearDisplacementCache()
    }
  }, [manuallySelectedFrameIndex])

  // Auto-select frame based on holy red dot proximity using centralized selection hook
  // Use ref to access current framesToRender without triggering effect
  const framesToRenderRef = useRef<typeof framesToRender>([])
  framesToRenderRef.current = framesToRender
  
  useEffect(() => {
    // Check if data is still loading
    const state = useAppStore.getState()
    if (state.isLoadingData) {
      return
    }
    
    // 🎬 CRITICAL: Skip auto-select if animation is running - CHECK FIRST, BEFORE THROTTLE
    if (state.isFrameSequenceAnimating) {
      console.log(`⏭️ [AUTO_SELECT] Skipping - animation in progress`)
      return
    }

    if (framesToRenderRef.current.length === 0) {
      return
    }

    // Throttle auto-select calls to prevent excessive re-renders (only after animation check)
    const now = Date.now()
    
    if (now - lastAutoSelectTimeRef.current < 200) {
      return
    }
    lastAutoSelectTimeRef.current = now
    
    // Skip auto-select if currently panning
    if (state.isPanning) {
      return
    }

    // Check if user just manually selected a frame (within last 300ms)
    const timeSinceManualSelection = Date.now() - state.lastManualSelectionTime
    const recentlyManuallySelected = timeSinceManualSelection < 300
    
    if (recentlyManuallySelected) {
      return
    }

    // Try centralized frame selection with 'auto' trigger
    selectFrame(
      { worldX: worldUnderRedDot.x, worldY: worldUnderRedDot.y },
      'auto',
      imageDimensions || undefined
    )
    
    // Reset lastAutoSelected when user manually selects
    if (recentlyManuallySelected && manuallySelectedFrameIndex !== lastAutoSelectedRef.current) {
      lastAutoSelectedRef.current = null
    }
  }, [
    worldUnderRedDot,
    selectFrame,
    imageDimensions,
    manuallySelectedFrameIndex,
    isFrameSequenceAnimating  // 🔴 ADD THIS - force effect to re-run when animation state changes
  ])

  // 🆕 Clear persistent context frames when showExtraContextFrames is disabled
  useEffect(() => {
    if (!navigationSettings.showExtraContextFrames) {
      // console.log('🧹 Clearing persistent context frames because showExtraContextFrames is disabled')
      setPersistentContextFrames(new Set())
    }
  }, [navigationSettings.showExtraContextFrames])
  
  // Track if the tap indicator was manually panned (so we don't recalculate position)
  const [tapIndicatorPannedPosition, setTapIndicatorPannedPosition] = useState<{ x: number; y: number } | null>(null)

  // Programmatic tap: when 3D data is ready and we have shared URL params, simulate a tap
  // This runs camera discovery in background so the indicator has full data when user activates it
  useEffect(() => {
    if (!sharedItemParams) return
    if (!canUseTapIndicator) return
    // Only run once — if tapIndicator is already set, skip
    if (tapIndicator) return

    const targetFrame = sharedItemParams.targetFrame
    const targetCamera = cameras[targetFrame]
    if (!targetCamera) return

    // Find the selected frame element to get its rect
    const frameElement = document.querySelector('[data-selected-frame="true"]') as HTMLDivElement
    if (!frameElement) return

    const rect = frameElement.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return

    // Convert image pixel coords to display coords for calculateCameraVisibility
    const clickX = (sharedItemParams.x / targetCamera.intrinsics.width) * rect.width
    const clickY = (sharedItemParams.y / targetCamera.intrinsics.height) * rect.height

    console.log(`\ud83d\udce9 [Shared URL] Running programmatic tap at image (${sharedItemParams.x.toFixed(1)}, ${sharedItemParams.y.toFixed(1)}) \u2192 display (${clickX.toFixed(1)}, ${clickY.toFixed(1)})`)

    const appState = useAppStore.getState()
    const { highlightedCameras: camerasAtLocation, projections, point3D } = calculateCameraVisibility(
      clickX, clickY, rect, targetCamera, cameras, appState.points3D, offsetMap, targetFrame
    )

    const cameraCount = camerasAtLocation.size
    const imagePixelX = sharedItemParams.x
    const imagePixelY = sharedItemParams.y

    // Calculate 3D radius for sphere projection
    const depth = point3D ? calculateCameraSpaceDepth(point3D, targetCamera) : 1.0
    const focalLength = calculateFocalLength(targetCamera)
    const circleRadiusMeters = (sharedItemParams.r * depth) / focalLength

    // Use the shared URL radius directly (it's already in image space)
    const radiusPxImageSpace = sharedItemParams.r

    // Calculate screen position
    const containerRect = containerRef.current?.getBoundingClientRect()
    const screenX = containerRect ? rect.left - containerRect.left + clickX : clickX
    const screenY = containerRect ? rect.top - containerRect.top + clickY : clickY

    console.log(`\ud83d\udce9 [Shared URL] Discovery found ${cameraCount} cameras, setting tap indicator in preview mode`)

    setTapIndicatorPannedPosition(null)
    setTapIndicatorPreviewMode(true)
    setTapIndicator({
      show: true,
      cameraCount,
      screenPosition: { x: screenX, y: screenY },
      frameRelativeTapX: clickX,
      frameRelativeTapY: clickY,
      imagePixelX,
      imagePixelY,
      currentDisplayX: clickX,
      currentDisplayY: clickY,
      frameCamera: targetCamera,
      frameIndex: targetFrame,
      tappedFrameIndex: targetFrame,
      projections,
      point3D,
      radiusPxImageSpace,
      circleRadiusMeters,
    })

    // Clear shared params so this doesn't re-run
    setSharedItemParams(null)
  }, [sharedItemParams, canUseTapIndicator, tapIndicator, cameras, offsetMap])

  // 🆕 When frame changes during pan, update tapIndicator to follow to the new frame
  // This reuses the same projections map (already has projected positions for connected frames)
  useEffect(() => {
    if (!tapIndicator || !tapIndicator.show || !tapIndicator.projections) return
    if (manuallySelectedFrameIndex === null || manuallySelectedFrameIndex === undefined) return
    if (manuallySelectedFrameIndex === tapIndicator.frameIndex) return

    // Check if the new frame has a projection for this 3D point
    const projectedPixel = tapIndicator.projections.get(manuallySelectedFrameIndex)
    if (!projectedPixel) {
      console.log(`🔴 [TapIndicator Pan] Frame ${manuallySelectedFrameIndex} has no projection, hiding indicator`)
      return
    }

    const newCamera = cameras[manuallySelectedFrameIndex]
    if (!newCamera) return

    console.log(`🔴 [TapIndicator Pan] Frame changed ${tapIndicator.frameIndex} → ${manuallySelectedFrameIndex}, updating indicator`)

    // Update tapIndicator to point to new frame (keep immutable fields)
    setTapIndicator(prev => {
      if (!prev) return prev
      return {
        ...prev,
        frameIndex: manuallySelectedFrameIndex,
        frameCamera: newCamera,
        scaleData: undefined, // Clear per-camera 3D scale data — stale values cause radius flash
      }
    })

    // Sync orbitIndex to the new frame so ◀ ▶ buttons start from here after a pan
    const store = useAppStore.getState()
    const orbitSeq = store.orbitSequence
    if (orbitSeq) {
      const newOrbitIdx = orbitSeq.findIndex(e => e.frameIndex === manuallySelectedFrameIndex)
      if (newOrbitIdx >= 0 && newOrbitIdx !== store.orbitIndex) {
        store.setOrbitIndex(newOrbitIdx)
      }
    }

    // Clear any manually panned position so projection recalculates
    setTapIndicatorPannedPosition(null)
  }, [manuallySelectedFrameIndex])

  // Wrap updateRadius to also persist radius in tapIndicator state
  // Discovery hook silently bails if thumbnails aren't visible, leaving radiusPxImageSpace stale
  const handleUpdateRadius = useCallback((radiusPx: number) => {
    updateRadius(radiusPx)
    setTapIndicator(prev => {
      if (!prev) return prev
      return { ...prev, radiusPxImageSpace: radiusPx }
    })
  }, [updateRadius])

  // Handle tap indicator pan complete - update camera discovery at new location
  const handleTapIndicatorPanComplete = useCallback((finalScreenX: number, finalScreenY: number, finalDisplayX: number, finalDisplayY: number) => {
    if (!tapIndicator) return
    
    console.log('[NewModeViewer] Tap indicator pan complete')
    console.log('   Screen coords:', finalScreenX.toFixed(1), finalScreenY.toFixed(1))
    console.log('   Unscaled display coords (for position):', finalDisplayX.toFixed(1), finalDisplayY.toFixed(1))
    console.log('📌 [Pan Complete] IMPORTANT: This updates VISUAL position only.')
    console.log('   Original frameRelativeTap coordinates remain immutable:', {
      frameRelativeTapX: tapIndicator.frameRelativeTapX,
      frameRelativeTapY: tapIndicator.frameRelativeTapY
    })
    
    // Get frame element to run camera discovery
    const frameElement = document.querySelector('[data-selected-frame="true"]') as HTMLDivElement
    if (!frameElement) {
      console.warn('[Pan Complete] Frame element not found')
      return
    }
    
    const rect = frameElement.getBoundingClientRect()
    
    // STORE THE PANNED POSITION so it won't be recalculated by the parent
    setTapIndicatorPannedPosition({ x: finalDisplayX, y: finalDisplayY })
    
    // Convert unscaled display coordinates to scaled coordinates for 3D lookup
    // Unscaled coords need to be multiplied by zoom to match what calculateCameraVisibility expects
    const scaledPanX = finalDisplayX * zoomScale
    const scaledPanY = finalDisplayY * zoomScale
    
    console.log('   Converting unscaled (', finalDisplayX.toFixed(1), ',', finalDisplayY.toFixed(1), ') to scaled (', scaledPanX.toFixed(1), ',', scaledPanY.toFixed(1), ') at zoom', zoomScale.toFixed(2))
    
    // Run camera discovery at panned location
    const result = calculateCameraVisibility(
      scaledPanX,
      scaledPanY,
      rect,
      tapIndicator.frameCamera,
      cameras,
      useAppStore.getState().points3D,
      offsetMap,
      tapIndicator.frameIndex
    )
    
    if (result) {
      const cameraCount = result.cameraCount
      console.log('[Pan Complete] Found', cameraCount, 'cameras at new location')
      console.log('[Pan Complete] New 3D point:', result.point3D)
      
      // 🔧 CRITICAL: Recalculate image pixel coordinates for the new panned position
      // Get the CURRENT frame element to get accurate container dimensions
      const frameElement = document.querySelector('[data-selected-frame="true"]') as HTMLDivElement
      if (!frameElement) {
        console.warn('[Pan Complete] Frame element not found for recalculation')
        return
      }
      
      const currentRect = frameElement.getBoundingClientRect()
      // 🔧 CRITICAL: Use SCALED coordinates to match what calculateCameraVisibility used
      // Must match the normalization that happened during 3D lookup
      const newImagePixelX = (scaledPanX / currentRect.width) * tapIndicator.frameCamera.intrinsics.width
      const newImagePixelY = (scaledPanY / currentRect.height) * tapIndicator.frameCamera.intrinsics.height
      
      console.log('[Pan Complete] Recalculating imagePixels for new panned location:')
      console.log(`   Scaled display coords: (${scaledPanX.toFixed(1)}, ${scaledPanY.toFixed(1)}) [zoom-adjusted]`)
      console.log(`   Container: ${currentRect.width.toFixed(0)}×${currentRect.height.toFixed(0)}`)
      console.log(`   New imagePixels: (${newImagePixelX.toFixed(1)}, ${newImagePixelY.toFixed(1)}) [matches 3D lookup]`)
      
      // Update tap indicator position with NEW 3D data at panned location
      // The position change will trigger auto-trigger to regenerate thumbnails after 500ms
      const preferredRadius = useAppStore.getState().preferredDiscoveryRadiusPx
      setTapIndicator({
        show: true,
        cameraCount: result.cameraCount, // Update with cameras at NEW location
        screenPosition: { x: finalScreenX, y: finalScreenY }, // Screen position for UI
        frameRelativeTapX: tapIndicator.frameRelativeTapX,    // IMMUTABLE: Keep original tap!
        frameRelativeTapY: tapIndicator.frameRelativeTapY,    // IMMUTABLE: Keep original tap!
        imagePixelX: newImagePixelX,                          // UPDATE: New image pixels for panned location
        imagePixelY: newImagePixelY,                          // UPDATE: New image pixels for panned location
        currentDisplayX: scaledPanX,                        // UPDATE: New panned position (triggers auto-trigger)
        currentDisplayY: scaledPanY,                        // UPDATE: New panned position (triggers auto-trigger)
        frameCamera: tapIndicator.frameCamera,
        frameIndex: tapIndicator.frameIndex,
        projections: result.projections, // Update with NEW projections
        point3D: result.point3D, // Update with NEW 3D point
        radiusPxImageSpace: preferredRadius ?? tapIndicator.radiusPxImageSpace,
        scaleData: tapIndicator.scaleData,                   // PRESERVE: Keep scale data for consistent radius
        circleRadiusMeters: tapIndicator.circleRadiusMeters, // PRESERVE: Keep 3D radius
      })
      
      console.log('[Pan Complete] Position updated - user can tap indicator to show thumbnails')
      // NOTE: Thumbnails will NOT auto-show after pan - user must tap indicator to trigger discovery
    }
  }, [tapIndicator, cameras, offsetMap, zoomScale, showThumbnails])

  // Show resolution tier badge when zoom crosses SD/HD/4K thresholds
  useEffect(() => {
    const tier: ResolutionTierLabel =
      zoomScale > 3.0 ? '4K'
      : zoomScale > 1.5 ? 'HD'
      : 'Standard'

    if (tier !== prevResolutionTierRef.current) {
      console.log(`[ResolutionTier] Switched: ${prevResolutionTierRef.current} → ${tier} (zoomScale=${zoomScale.toFixed(2)})`)
      prevResolutionTierRef.current = tier
      setResolutionBadge(tier)
      if (resolutionBadgeTimerRef.current !== null) {
        clearTimeout(resolutionBadgeTimerRef.current)
      }
      resolutionBadgeTimerRef.current = window.setTimeout(() => {
        setResolutionBadge(null)
        resolutionBadgeTimerRef.current = null
      }, 3000)
    }
  }, [zoomScale])

  return (
    <>
           
      
      {/* IMAGE VIEWER SECTION - Top 80% - NO CSS CLASS */}
      <div
        ref={containerRef}
        onClick={(e) => {
          const target = e.target as any
          let classNameStr = 'no-class'
          if (target?.className) {
            if (typeof target.className === 'string') {
              classNameStr = target.className
            } else if (target.className?.baseVal) {
              classNameStr = target.className.baseVal
            }
          }
          console.log(`\n📌 [CONTAINER CLICK]`, {
            target: target?.tagName,
            class: classNameStr,
            clientX: e.clientX,
            clientY: e.clientY
          })
          
          // 🎬 Cancel frame sequence animation on any viewport click
          const appState = useAppStore.getState()
          if (appState.isFrameSequenceAnimating) {
            console.log(`🎬 [ViewportClick] Cancelling frame sequence animation`)
            appState.cancelFrameSequenceAnimation()
            return
          }
          
          // Pause playback when tapping on viewer during playback
          if (playback.isPlaying) {
            stopPlayback()
          }
        }}
        onTouchStart={() => {
          // 🎬 Cancel ALL animations on any touch
          const appState = useAppStore.getState()
          if (appState.isFrameSequenceAnimating) {
            console.log(`🎬 [TouchStart] Cancelling frame sequence animation`)
            appState.cancelFrameSequenceAnimation()
          }
          // Also cancel shop preview animation that might be running in background
          getShopCardPreviewAnimationService().cancel()
        }}
        onTouchEnd={() => {
          // 🎬 Cancel frame sequence animation on any touch end
          const appState = useAppStore.getState()
          if (appState.isFrameSequenceAnimating) {
            console.log(`🎬 [TouchEnd] Cancelling frame sequence animation`)
            appState.cancelFrameSequenceAnimation()
          }
        }}
        onPointerDown={(e) => {
          // Long-press detection for owner context menu (rescan)
          if (!isCurrentUserOwner) return
          cancelLongPress()
          const px = e.clientX
          const py = e.clientY
          longPressTimerRef.current = window.setTimeout(() => {
            longPressTimerRef.current = null
            const cam = cameras[currentCameraIndex]
            setContextMenu({
              open: true,
              x: px,
              y: py,
              frameIndex: currentCameraIndex,
              frameFile: cam?.frame_file || '',
            })
          }, 600)
        }}
        onPointerUp={() => cancelLongPress()}
        onPointerMove={() => cancelLongPress()}
        style={{ 
          width: '100vw',
          height: showDebugOverlay ? '80vh' : '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#000',
          overflow: 'hidden',
          borderBottom: showDebugOverlay ? '2px solid #333' : 'none',
          zIndex: 100,
          touchAction: 'manipulation',
        }}
      >
          {/* Resolution tier badge — briefly shown when zoom crosses SD/HD/4K threshold */}
          {resolutionBadge && (
            <div
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 9999,
                background:
                  resolutionBadge === '4K' ? 'rgba(180,100,255,0.92)'
                  : resolutionBadge === 'HD' ? 'rgba(0,160,255,0.92)'
                  : 'rgba(80,80,80,0.88)',
                color: '#fff',
                fontFamily: 'monospace',
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 1,
                padding: '5px 12px',
                borderRadius: 20,
                pointerEvents: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                animation: 'resoBadgeFade 3s ease forwards',
              }}
            >
              {resolutionBadge === '4K' ? '🔵 4K' : resolutionBadge === 'HD' ? '🟦 HD' : '⬜ Standard'}
            </div>
          )}

          {!imageLoaded && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #3498db',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              ></div>
            </div>
          )}

          {/* Viewport with pan & zoom transform */}
          <SimpleZoomPan externalTransform={{ scale: zoomScale, translateX: totalViewportOffset.x, translateY: totalViewportOffset.y }}>
            {/* Warm-start frame (frame_0000.jpg) displayed immediately while manifest loads */}
            {warmStartFrame && cameras.length === 0 && (
              <img
                src={warmStartFrame}
                alt="Loading..."
                style={{
                  position: 'absolute',
                  left: `${5000 - effectiveDisplayWidth / 2}px`,
                  top: `${5000 - effectiveDisplayHeight / 2}px`,
                  width: `${effectiveDisplayWidth}px`,
                  height: `${effectiveDisplayHeight}px`,
                  objectFit: 'contain',
                  zIndex: 999,
                  opacity: 0.95,
                  pointerEvents: 'none'
                }}
              />
            )}
            <div
              className="canvas"
              onClick={(e) => {
                const target = e.target as any
                let classNameStr = 'no-class'
                if (target?.className) {
                  if (typeof target.className === 'string') {
                    classNameStr = target.className
                  } else if (target.className?.baseVal) {
                    classNameStr = target.className.baseVal
                  }
                }
                console.log(`\n🎨 [CANVAS CLICK]`, {
                  target: target?.tagName,
                  class: classNameStr,
                  id: target?.id,
                  clientX: e.clientX,
                  clientY: e.clientY
                })
                
                // 🎬 Cancel frame sequence animation on any canvas click
                const appState = useAppStore.getState()
                if (appState.isFrameSequenceAnimating) {
                  console.log(`🎬 [CanvasClick] Cancelling frame sequence animation`)
                  appState.cancelFrameSequenceAnimation()
                  return
                }
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '10000px',
                height: '10000px',
              }}
            >
              {/* BACKGROUND FRAMES LAYER - All frames with blur */}
              {/* NOTE: Selected frame is rendered here too (blurred) AND in the selected layer (sharp).
                  This prevents flicker: when selection changes, the new selected frame's <img> takes
                  1 paint cycle to appear in the sharp layer. The blurred copy acts as a fallback,
                  so you never see through to empty space. The sharp selected layer (z-index 30000)
                  fully covers the blurred duplicate once painted. */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '10000px',
                  height: '10000px',
                  filter: (settings.enableFrameBlur && !showDebugOverlay)
                    ? (isPanningRef.current ? 'blur(5px)' : 'blur(4px)')
                    : 'none',
                  transition: isPanningRef.current ? 'none' : 'filter 0.3s ease-out',
                }}
              >
                {/* Render ALL frames (including selected — it's covered by sharp layer above) */}
                {framesToRender.map((framePos) => {
                const frameIndex = framePos.frameIndex
                const frameCamera = cameras[frameIndex]
                const isSelected = frameIndex === manuallySelectedFrameIndex

                if (!frameCamera) {
                  // console.warn(`⚠️ [Render] Frame ${frameIndex} has no camera data`)
                  return null
                }
                
                // Check if this is a context frame (extended connectivity, not directly connected)
                // Only mark as context frame when showExtraContextFrames is enabled
                // Calculate locally instead of using state to avoid circular dependencies
                const isContextFrame = navigationSettings.showExtraContextFrames && 
                                      framePos.frameIndex !== manuallySelectedFrameIndex && 
                                      !connectedFrameIndices.has(framePos.frameIndex)                // Calculate 3D distance from camera to world origin for z-index
                // Calculate z-index based on 3D distance from selected frame
                // Closer frames get higher z-index (drawn on top)
                let zIndexValue = 1000
                
                if (
                  manuallySelectedFrameIndex !== null &&
                  cameras[manuallySelectedFrameIndex]?.position &&
                  frameCamera.position
                ) {
                  const selectedCam = cameras[manuallySelectedFrameIndex]
                  const [x1, y1, z1] = selectedCam.position
                  const [x2, y2, z2] = frameCamera.position
                  
                  // Calculate 3D distance from selected frame to this frame
                  const distance3d = Math.sqrt(
                    (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2
                  )
                  
                  // Invert distance: smaller distance = higher z-index
                  // Normalize to range 1000-10000 (higher distance = lower z-index)
                  zIndexValue = Math.max(1000, 10000 - Math.floor(distance3d * 100))
                }

                // Selected frame always on top (highest priority)
                // BACKDROP FILTER: Selected frame must be above blur overlay (z-index 20000)
                if (isSelected) {
                  zIndexValue = 30000 // Above blur overlay
                  // 🎬 DEBUG: Log selected frame during animation
                  if (isFrameSequenceAnimating && frameIndex % 10 === 0) {
                    console.log(`🎬 [ANIMATION] Frame ${frameIndex} isSelected: ${isSelected}, z-index: ${zIndexValue}, manuallySelectedFrameIndex: ${manuallySelectedFrameIndex}`)
                  }
                }

                // Context frames get lower z-index than filtered frames
                if (isContextFrame) {
                  zIndexValue = Math.max(500, zIndexValue - 500)
                }

                // Determine if frame is hovered (from viewer or candidate list)
                const isHovered = hoveredCandidateFrameIndex === frameIndex
                
                // Hovered frame on top of everything (only if not panning)
                if (isHovered && !isPanningRef.current) {
                  zIndexValue = 40000 // Above blur overlay
                  if (isFrameSequenceAnimating) {
                    console.log(`⚠️ [ANIMATION] Frame ${frameIndex} is HOVERED during animation! z-index jumped to ${zIndexValue}`)
                  }
                }

                // 🎬 DEBUG: Log z-index for all rendered frames during animation (first few frames only)
                if (isFrameSequenceAnimating && animatingFrameIndex !== null && animatingFrameIndex < 3) {
                  console.log(`🎬 [ANIMATION Frame ${animatingFrameIndex}] Rendering frame ${frameIndex}: isSelected=${isSelected}, zIndex=${zIndexValue}`)
                }

                // Position on canvas - world coordinates now represent frame CENTERS
                // Convert to top-left by subtracting half the display dimensions
                const displayCenterX = effectiveDisplayWidth / 2
                const displayCenterY = effectiveDisplayHeight / 2
                let positionX = framePos.worldX - displayCenterX
                let positionY = framePos.worldY - displayCenterY
                let hoverOffsetInfo: { dx: number; dy: number; dz: number } | null = null

                // Log first few frames for debugging
                // if (frameIndex <= 5 || frameIndex === manuallySelectedFrameIndex) {
                //   console.log(`📍 [Frame ${frameIndex}] Initial position:`, {
                //     worldX: framePos.worldX.toFixed(1),
                //     worldY: framePos.worldY.toFixed(1),
                //     displayCenterX: displayCenterX.toFixed(1),
                //     displayCenterY: displayCenterY.toFixed(1),
                //     positionX: positionX.toFixed(1),
                //     positionY: positionY.toFixed(1),
                //     isContext: isContextFrame,
                //   })
                // }

                // Position frames relative to selected frame
                if (manuallySelectedFrameIndex !== null && manuallySelectedFrameIndex !== frameIndex) {
                  const selectedFramePos = frameWorldPositions.find(p => p.frameIndex === manuallySelectedFrameIndex)

                  if (selectedFramePos) {
                    if (isContextFrame) {
                      // Context frames: calculate relative position from world positions directly
                      // Don't use offset map - just subtract selected frame position from this frame's position
                      const displacement = {
                        x: framePos.worldX - selectedFramePos.worldX,
                        y: framePos.worldY - selectedFramePos.worldY
                      }
                      
                      positionX = selectedFramePos.worldX - displayCenterX + displacement.x
                      positionY = selectedFramePos.worldY - displayCenterY + displacement.y

                      hoverOffsetInfo = {
                        dx: displacement.x,
                        dy: displacement.y,
                        dz: 0
                      }
                    } else {
                      // Connected frames: use path finding with offset map
                      const graphToUse = connectivityGraph
                      
                      const displacement = getCachedFrameDisplacement(
                        manuallySelectedFrameIndex,
                        frameIndex,
                        graphToUse,
                        offsetMap
                      )

                      if (displacement) {
                        positionX = selectedFramePos.worldX - displayCenterX + displacement.x
                        positionY = selectedFramePos.worldY - displayCenterY + displacement.y

                        hoverOffsetInfo = {
                          dx: displacement.x,
                          dy: displacement.y,
                          dz: 0
                        }
                      }
                    }
                  }
                }

                return (
                  <div key={`frame-${frameIndex}`}>
                    {/* Frame container */}
                    <div
                      // DISABLED FOR TESTING: onDoubleClick, onTouchEnd, onMouseEnter/Leave
                      // onDoubleClick={(e) => {
                      //   // Any frame can be selected via double-click
                      //   console.log(`🎯 Frame ${frameIndex} selected via double-click`)
                      //   console.log(`   isContextFrame: ${isContextFrame}, frameIndex: ${frameIndex}`)
                      //   useAppStore.getState().setManuallySelectedFrame(frameIndex)
                      //   e.stopPropagation()
                      // }}
                      // onTouchEnd={() => {
                      //   // Any frame can be selected via touch
                      //   console.log(`🎯 Frame ${frameIndex} selected via touch`)
                      //   console.log(`   isContextFrame: ${isContextFrame}, frameIndex: ${frameIndex}`)
                      //   useAppStore.getState().setManuallySelectedFrame(frameIndex)
                      // }}
                      // onMouseEnter={() => {
                      //   // Disable hover during pan to avoid interfering with test
                      //   if (!isPanningRef.current) {
                      //     setHoveredFrameIndex(frameIndex)
                      //   }
                      // }}
                      // onMouseLeave={() => {
                      //   if (!isPanningRef.current) {
                      //     setHoveredFrameIndex(null)
                      //   }
                      // }}
                      style={{
                        position: 'absolute',
                        left: `${positionX}px`,
                        top: `${positionY}px`,
                        width: `${effectiveDisplayWidth}px`,
                        height: `${effectiveDisplayHeight}px`,
                        zIndex: zIndexValue,
                        opacity: isSelected ? 1.0 : (isContextFrame ? 0.4 : 0.85),
                        // NO per-frame blur - using backdrop-filter overlay instead (much better performance!)
                        // 🎬 Only allow opacity transition for SECTION_PLAY_ALL cross-fades.
                        // All other navigation (NSEW, explore orbit, instant jumps) must snap instantly
                        // to prevent flicker: during a 300ms fade, both old and new frames are semi-transparent
                        // which reveals the blurred background layer underneath.
                        transition: (isFrameSequenceAnimating && animationTrigger === 'SECTION_PLAY_ALL')
                          ? 'opacity 0.3s ease-out'
                          : 'none',
                        border: (() => {
                          if (!navigationSettings.showFrameBoundariesAndLabels) return 'none'
                          
                          let borderColor = ''
                          if (isSelected) {
                            borderColor = '6px solid #00FF00' // Green for selected
                          } else if (isContextFrame) {
                            borderColor = '2px solid #0000FF' // Blue for context
                          } else {
                            borderColor = '2px solid #FF9500' // Orange for connected
                          }
                          
                          // DEBUG: Log border color for specific frames
                          if ((frameIndex === 0 || frameIndex === 1 || frameIndex === 33) && hoveredCandidateFrameIndex === frameIndex) {
                            // console.log(`🎨 [Border] Frame ${frameIndex}: isSelected=${isSelected}, isContext=${isContextFrame}, border=${borderColor}`)
                          }
                          
                          return borderColor
                        })(),
                        boxSizing: 'border-box',
                        pointerEvents: 'auto',
                        cursor: isContextFrame ? 'pointer' : 'pointer', // Context frames also clickable
                        outline: navigationSettings.showFrameBoundariesAndLabels && isSelected
                          ? '2px solid #00FF00'
                          : 'none',
                      }}
                      onMouseMove={() => {
                        // Frame position tracking on hover (disabled)
                      }}
                    >
                      {/* Frame image */}
                      {missingFrames.has(frameCamera.frame_file) ? (
                        <div
                          style={{
                            position: 'absolute',
                            left: 0, top: 0, width: '100%', height: '100%',
                            background: 'rgba(30,30,30,0.85)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: 11,
                            fontFamily: 'monospace',
                          }}
                        >
                          {frameCamera.frame_file}
                        </div>
                      ) : (
                        <img
                          src={getFrameImagePath(shopId, frameCamera.frame_file, currentSectionFramesPath || undefined)}
                          alt={`Frame ${frameIndex}`}
                          draggable={false}
                          onError={() => {
                            markFrameMissing(frameCamera.frame_file)
                          }}
                          style={{
                            position: 'absolute',
                            left: '0',
                            top: '0',
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      )}

                      {/* Frame label - top left corner */}
                      {navigationSettings.showFrameBoundariesAndLabels && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '5px',
                            left: '5px',
                            background: isSelected 
                              ? 'rgba(0, 255, 0, 0.9)' 
                              : isContextFrame
                                ? 'rgba(0, 0, 255, 0.9)' // Blue for context frames
                                : hoveredCandidateFrameIndex === frameIndex
                                  ? 'rgba(0, 255, 0, 0.9)'
                                  : 'rgba(255, 165, 0, 0.7)', // Orange for connected frames
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '3px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            pointerEvents: 'none',
                            zIndex: 100,
                            opacity: 1,
                          }}
                        >
                          <div>
                            {isContextFrame 
                              ? `Context ${frameIndex}` 
                              : hoverOffsetInfo && hoveredCandidateFrameIndex === frameIndex
                                ? `Frame ${frameIndex} (dx=${hoverOffsetInfo.dx.toFixed(1)}, dy=${hoverOffsetInfo.dy.toFixed(1)}, dz=${hoverOffsetInfo.dz.toFixed(1)})`
                                : `Frame ${frameIndex}`}
                          </div>
                          <div style={{ fontSize: '10px', fontWeight: 'normal', marginTop: '2px', opacity: 0.9 }}>
                            {frameCamera.frame_file}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
              </div>

              {/* SELECTED FRAME LAYER - Renders on top with light blur during pan */}
              {manuallySelectedFrameIndex !== null && (() => {
                const selectedFramePos = framesToRender.find(fp => fp.frameIndex === manuallySelectedFrameIndex)
                if (!selectedFramePos) return null
                
                const frameIndex = selectedFramePos.frameIndex
                const frameCamera = cameras[frameIndex]
                if (!frameCamera) return null

                // const isSelected = true
                // const isContextFrame = false
                const zIndexValue = 200000 // Always on top — above CameraTrajectoryOverlay (100001)
                // const isHovered = hoveredFrameIndex === frameIndex || hoveredCandidateFrameIndex === frameIndex

                const displayCenterX = effectiveDisplayWidth / 2
                const displayCenterY = effectiveDisplayHeight / 2
                const positionX = selectedFramePos.worldX - displayCenterX
                const positionY = selectedFramePos.worldY - displayCenterY

                return (
                  <div key="selected-frame-layer">
                    <div
                      data-selected-frame="true"
                      onClick={(e) => {
                        // If click originated from inside tap indicator, bail out
                        const clicked = e.target as HTMLElement
                        if (clicked.closest('[data-tap-chat], [data-tap-close], [data-tap-master-blocker], [data-tap-disk-blocker], [data-tap-mark], [data-chat-balloon]')) {
                          console.log('[SINGLE TAP] Ignoring — click from tap indicator child')
                          return
                        }
                        // Removed e.stopPropagation() to allow mobile overlay to work alongside camera discovery
                        console.log(`\n🖱️ [SINGLE TAP HANDLER FIRED]`)
                        
                        // Handle single-tap to show simple indicator at tap location
                        const containerDiv = e.currentTarget as HTMLDivElement
                        const rect = containerDiv.getBoundingClientRect()
                        const clickX = e.clientX - rect.left
                        const clickY = e.clientY - rect.top
                        
                        console.log(`🖼️ [Simple Tap Indicator]`, {
                          frameIndex,
                          clickTarget: {
                            element: (e.target as HTMLElement).tagName,
                            class: (e.target as HTMLElement).className
                          },
                          displayClickCoords: { x: clickX.toFixed(1), y: clickY.toFixed(1) },
                          containerRect: { left: rect.left, top: rect.top, width: rect.width.toFixed(0), height: rect.height.toFixed(0) },
                          imageSizePixels: { width: frameCamera.intrinsics.width, height: frameCamera.intrinsics.height }
                        })
                        
                        // Convert click coordinates to world coordinates for indicator positioning
                        // The indicator is rendered inside SimpleZoomPan which uses world coordinates
                        const worldX = positionX + clickX
                        const worldY = positionY + clickY
                        
                        console.log(`📍 [Simple Indicator Position] Display: (${clickX.toFixed(1)}, ${clickY.toFixed(1)}) → World: (${worldX.toFixed(1)}, ${worldY.toFixed(1)})`)
                        
                        // Calculate screen position after SimpleZoomPan transformation
                        // SimpleZoomPan applies: screen = world * zoom + offset
                        const screenX = worldX * zoomScale + totalViewportOffset.x
                        const screenY = worldY * zoomScale + totalViewportOffset.y
                        
                        console.log(`📍 [Screen Position] World: (${worldX.toFixed(1)}, ${worldY.toFixed(1)}) → Screen: (${screenX.toFixed(1)}, ${screenY.toFixed(1)})`)
                        
                        // Calculate camera visibility at tap location
                        console.log(`🔍 [Camera Count] Calculating cameras visible at tap location...`)
                        const appState = useAppStore.getState()
                        const { highlightedCameras: camerasAtLocation, projections, point3D } = calculateCameraVisibility(
                          clickX,
                          clickY,
                          rect,
                          frameCamera,
                          cameras,
                          appState.points3D,
                          offsetMap,
                          frameIndex
                        )
                        
                        let cameraCount = camerasAtLocation.size
                        const is2DOnly = cameraCount === 0
                        if (is2DOnly) {
                          cameraCount = 1 // 2D fallback: current frame counts as 1
                          console.log(`⚠️ [2D Fallback] No 3D point found — entering 2D-only mode (cameraCount forced to 1)`)
                        }
                        console.log(`✅ [Camera Count] Found ${cameraCount} cameras visible at tap location${is2DOnly ? ' (2D-only mode)' : ''}`)
                        
                        // 🔧 CRITICAL: Calculate image pixel coordinates at tap time
                        // These are zoom-independent and will be used for discovery after zoom changes
                        const imagePixelX = (clickX / rect.width) * frameCamera.intrinsics.width
                        const imagePixelY = (clickY / rect.height) * frameCamera.intrinsics.height
                        console.log(`🎯 [Image Pixels] Tap at display (${clickX.toFixed(1)}, ${clickY.toFixed(1)}) → image space (${imagePixelX.toFixed(1)}, ${imagePixelY.toFixed(1)})`)
                        console.log(`   Container: ${rect.width.toFixed(0)}×${rect.height.toFixed(0)}, Frame intrinsics: ${frameCamera.intrinsics.width}×${frameCamera.intrinsics.height}`)
                        
                        // Reset panned position when creating new tap indicator (start fresh)
                        setTapIndicatorPannedPosition(null)
                        
                        // Calculate initial radius (10% of image width as default)
                        const initialRadiusPx = frameCamera.intrinsics.width * 0.10
                        
                        // Calculate initial 3D radius from 2D using reverse projection: radiusMeters = (radiusPx * depth) / focalLength
                        const depth = point3D ? calculateCameraSpaceDepth(point3D, frameCamera) : 1.0
                        const focalLength = calculateFocalLength(frameCamera)
                        const circleRadiusMeters = (initialRadiusPx * depth) / focalLength
                        
                        console.log(`🔵 [Initial 3D Radius Calculation]`)
                        console.log(`   2D radius: ${initialRadiusPx.toFixed(1)}px`)
                        console.log(`   Depth: ${depth.toFixed(2)}m`)
                        console.log(`   Focal length: ${focalLength.toFixed(1)}px`)
                        console.log(`   → 3D radius: ${circleRadiusMeters.toFixed(3)}m`)
                        
                        // Show indicator with camera count (no camera count, no navigation)
                        console.log('🎯 [NewModeViewer] Setting tap indicator state:', {
                          screenPosition: { x: screenX, y: screenY },
                          frameRelativeTap: { x: clickX, y: clickY },
                          imagePixels: { x: imagePixelX.toFixed(1), y: imagePixelY.toFixed(1) },
                          cameraCount,
                          point3D,
                          initialRadiusPx
                        })
                        const preferredRadius = useAppStore.getState().preferredDiscoveryRadiusPx
                        setTapIndicatorPreviewMode(false) // Clear preview mode on normal tap
                        // For 2D-only mode, inject a synthetic projection at the tap position
                        // so the rendering gate doesn't bail out
                        const effectiveProjections = is2DOnly
                          ? new Map<number, [number, number]>([[frameIndex, [imagePixelX, imagePixelY]]])
                          : projections

                        setTapIndicator({
                          show: true,
                          cameraCount,
                          screenPosition: { x: screenX, y: screenY },      // For UI positioning
                          frameRelativeTapX: clickX,                       // Original tap: frame-relative
                          frameRelativeTapY: clickY,                       // Original tap: frame-relative
                          imagePixelX,                                     // Image space (zoom-independent)
                          imagePixelY,                                     // Image space (zoom-independent)
                          currentDisplayX: clickX,                         // Initially same as tap
                          currentDisplayY: clickY,
                          frameCamera,
                          frameIndex,
                          tappedFrameIndex: frameIndex,                    // IMMUTABLE: original tap frame (never changes during orbit)
                          projections: effectiveProjections,
                          point3D,
                          is2DOnly,                                        // 2D fallback flag
                          radiusPxImageSpace: is2DOnly ? undefined : (preferredRadius ?? initialRadiusPx),
                          circleRadiusMeters: is2DOnly ? undefined : circleRadiusMeters
                        })
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation()
                        console.log(`\n${'='.repeat(60)}`)
                        console.log(`🎯🎯 [DOUBLE-TAP HANDLER FIRED]`)
                        console.log(`${'='.repeat(60)}`)
                        
                        // Calculate rect and coordinates (same as single-tap)
                        const containerDiv = e.currentTarget as HTMLDivElement
                        const rect = containerDiv.getBoundingClientRect()
                        const clickX = e.clientX - rect.left
                        const clickY = e.clientY - rect.top
                        
                        // Convert to image pixel coordinates (zoom-independent)
                        const imagePixelX = (clickX / rect.width) * frameCamera.intrinsics.width
                        const imagePixelY = (clickY / rect.height) * frameCamera.intrinsics.height
                        
                        console.log(`📍 [DOUBLE-TAP] Step 1: Coordinate Calculation`)
                        console.log(`   Current frame: ${frameIndex}`)
                        console.log(`   Display click: (${clickX.toFixed(1)}, ${clickY.toFixed(1)})`)
                        console.log(`   Container size: ${rect.width.toFixed(0)}×${rect.height.toFixed(0)}`)
                        console.log(`   Frame intrinsics: ${frameCamera.intrinsics.width}×${frameCamera.intrinsics.height}`)
                        console.log(`   → Image pixel: (${imagePixelX.toFixed(1)}, ${imagePixelY.toFixed(1)})`)
                        
                        // Calculate world/screen coordinates for tap indicator
                        const worldX = positionX + clickX
                        const worldY = positionY + clickY
                        const screenX = worldX * zoomScale + totalViewportOffset.x
                        const screenY = worldY * zoomScale + totalViewportOffset.y
                        
                        // Get camera visibility for tap indicator
                        const appState = useAppStore.getState()
                        const { highlightedCameras: camerasAtLocation, projections, point3D } = calculateCameraVisibility(
                          clickX, clickY, rect, frameCamera, cameras, appState.points3D, offsetMap, frameIndex
                        )
                        const cameraCount = camerasAtLocation.size
                        
                        console.log(`🔍 [DOUBLE-TAP] Step 2: Camera Visibility Check`)
                        console.log(`   Cameras that can see this point: ${cameraCount}`)
                        console.log(`   3D point: ${point3D ? `[${point3D[0].toFixed(3)}, ${point3D[1].toFixed(3)}, ${point3D[2].toFixed(3)}]` : 'null'}`)
                        
                        // Show tap indicator for debugging
                        const initialRadiusPx = frameCamera.intrinsics.width * 0.10
                        const preferredRadius = appState.preferredDiscoveryRadiusPx
                        setTapIndicatorPannedPosition(null)
                        setTapIndicator({
                          show: true,
                          cameraCount,
                          screenPosition: { x: screenX, y: screenY },
                          frameRelativeTapX: clickX,
                          frameRelativeTapY: clickY,
                          imagePixelX,
                          imagePixelY,
                          currentDisplayX: clickX,
                          currentDisplayY: clickY,
                          frameCamera,
                          frameIndex,
                          projections,
                          point3D,
                          radiusPxImageSpace: preferredRadius ?? initialRadiusPx
                        })
                        console.log(`🔴 [DOUBLE-TAP] Step 3: Tap indicator shown (for debugging)`)
                        
                        // Get ranked cameras (best first)
                        console.log(`📊 [DOUBLE-TAP] Step 4: Getting ranked cameras...`)
                        const rankedCameras = getRankedNearbyCamerasByImagePixels(
                          imagePixelX,
                          imagePixelY,
                          frameCamera,
                          cameras,
                          appState.points3D,
                          offsetMap,
                          frameIndex,
                          10
                        )
                        
                        console.log(`📊 [DOUBLE-TAP] Ranked cameras (${rankedCameras.length} total):`)
                        rankedCameras.slice(0, 5).forEach((cam, i) => {
                          const isCurrent = cam.frameIndex === frameIndex ? ' ← CURRENT' : ''
                          console.log(`   #${i + 1}: Frame ${cam.frameIndex}, depth=${cam.distance.toFixed(2)}m${isCurrent}`)
                        })
                        
                        // Navigate to best camera (skip current frame)
                        const bestCamera = rankedCameras.find(c => c.frameIndex !== frameIndex)
                        
                        console.log(`🎯 [DOUBLE-TAP] Step 5: Navigation Decision`)
                        if (bestCamera) {
                          const targetFrame = bestCamera.frameIndex
                          const frameCount = Math.abs(targetFrame - frameIndex)
                          console.log(`   Best camera (excluding current): Frame ${targetFrame}`)
                          console.log(`   Depth: ${bestCamera.distance.toFixed(2)}m`)
                          console.log(`   Frame distance: ${frameCount} frames`)
                          
                          // For 1-frame jumps, skip animation overhead
                          if (frameCount <= 1) {
                            console.log(`🚀 [DOUBLE-TAP] Direct navigation (1 frame)`)
                            console.log(`${'='.repeat(60)}\n`)
                            selectCamera(targetFrame)
                          } else {
                            // Animate through frames like navigation hints
                            const timing = calculateFrameSequenceTiming(frameIndex, targetFrame)
                            
                            console.log(`🎬 [DOUBLE-TAP] Animation: ${frameIndex} → ${targetFrame} (${frameCount} frames)`)
                            console.log(`   Timing: ${timing.frameDuration}ms/frame, ${timing.framesToShow.length} frames, ~${timing.totalDuration}ms total`)
                            
                            // Set animation blocking state
                            appState.setFrameSequenceAnimating(true)
                            appState.setFrameSequenceAnimationTarget(targetFrame)
                            appState.setAnimatingFrameIndex(timing.framesToShow[0])
                            appState.setAnimationTrigger('DOUBLE_CLICK_ANIMATE')
                            
                            const animationService = getDoubleClickAnimationService()
                            const config = getAnimationConfig('DOUBLE_CLICK_ANIMATE')
                            
                            if (config && timing.framesToShow.length > 0) {
                              const dynamicConfig = { ...config, frameDuration: timing.frameDuration }
                              
                              const cancelFn = animationService.animateFrameSequence(
                                timing.framesToShow,
                                targetFrame,
                                dynamicConfig,
                                {
                                  onFrameShow: (frameIdx: number) => {
                                    console.log(`🎬 [DOUBLE-TAP] Showing frame: ${frameIdx}`)
                                    const freshState = useAppStore.getState()
                                    freshState.setAnimatingFrameIndex(frameIdx)
                                    freshState.centerViewportOnFrame(frameIdx, undefined, undefined, { skipAutoSelect: true })
                                  },
                                  onComplete: (finalFrame: number) => {
                                    console.log(`✅ [DOUBLE-TAP] Animation complete, frame: ${finalFrame}`)
                                    console.log(`${'='.repeat(60)}\n`)
                                    const freshState = useAppStore.getState()
                                    
                                    // Clear animation state
                                    freshState.setAnimatingFrameIndex(null)
                                    freshState.setAnimationTrigger(null)
                                    freshState.setFrameSequenceAnimating(false)
                                    freshState.setFrameSequenceAnimationCancel(null)
                                    freshState.setFrameSequenceAnimationTarget(null)
                                    
                                    // Select final frame
                                    freshState.setManuallySelectedFrame(finalFrame)
                                    freshState.centerViewportOnFrame(finalFrame)
                                  }
                                }
                              )
                              
                              appState.setFrameSequenceAnimationCancel(cancelFn)
                            } else {
                              // Fallback: direct navigation
                              console.log(`🚀 [DOUBLE-TAP] Fallback direct navigation`)
                              console.log(`${'='.repeat(60)}\n`)
                              selectCamera(targetFrame)
                            }
                          }
                        } else if (rankedCameras.length > 0) {
                          console.log(`   ⚠️ Only camera found is current frame (${frameIndex})`)
                          console.log(`   No navigation needed`)
                          console.log(`${'='.repeat(60)}\n`)
                        } else {
                          console.log(`   ⚠️ No cameras found at this location`)
                          console.log(`   (Tapped on empty space or no 3D points nearby)`)
                          console.log(`${'='.repeat(60)}\n`)
                        }
                      }}
                      style={{
                        position: 'absolute',
                        left: `${positionX}px`,
                        top: `${positionY}px`,
                        width: `${effectiveDisplayWidth}px`,
                        height: `${effectiveDisplayHeight}px`,
                        zIndex: zIndexValue,
                        opacity: 1.0,
                        filter: (settings.enableFrameBlur && !showDebugOverlay)
                          ? (isPanningRef.current && zoomScale < 1.5 ? 'blur(0.1px)' : 'none')
                          : 'none',
                        transition: isPanningRef.current ? 'none' : 'filter 0.3s ease-out',
                        border: navigationSettings.showFrameBoundariesAndLabels ? '6px solid #00FF00' : 'none',
                        boxSizing: 'border-box',
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        outline: navigationSettings.showFrameBoundariesAndLabels ? '2px solid #00FF00' : 'none',
                        imageRendering: 'auto'
                      }}
                    >
                      {missingFrames.has(frameCamera.frame_file) ? (
                        <div
                          style={{
                            width: '100%', height: '100%',
                            background: 'rgba(30,30,30,0.85)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'rgba(255,255,255,0.4)',
                            fontSize: 11,
                            fontFamily: 'monospace',
                          }}
                        >
                          {frameCamera.frame_file}
                        </div>
                      ) : (
                        <img
                          src={
                            zoomScale > 3.0 && !ultraFailedSet.current.has(frameCamera.frame_file)
                              ? getFrameUltraImagePath(shopId, frameCamera.frame_file, currentSectionFramesPath || undefined)
                              : zoomScale > 1.5 && !hdFailedSet.current.has(frameCamera.frame_file)
                                ? getFrameHdImagePath(shopId, frameCamera.frame_file, currentSectionFramesPath || undefined)
                                : getFrameImagePath(shopId, frameCamera.frame_file, currentSectionFramesPath || undefined)
                          }
                          alt={`Frame ${frameIndex}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'relative', zIndex: 1 }}
                          draggable={false}
                          onError={(e) => {
                            const img = e.target as HTMLImageElement
                            const stdSrc = getFrameImagePath(shopId, frameCamera.frame_file, currentSectionFramesPath || undefined)
                            const hdSrc = getFrameHdImagePath(shopId, frameCamera.frame_file, currentSectionFramesPath || undefined)

                            if (img.src.includes('_4k.')) {
                              // Ultra failed → fall back to HD
                              ultraFailedSet.current.add(frameCamera.frame_file)
                              img.src = hdSrc
                            } else if (img.src.includes('_hd.')) {
                              // HD failed → fall back to standard
                              hdFailedSet.current.add(frameCamera.frame_file)
                              img.src = stdSrc
                            } else {
                              // Standard SD failed — no more fallbacks
                              markFrameMissing(frameCamera.frame_file)
                            }
                          }}
                        />
                      )}

                      {/* Sticky boundaries circle - green dashed circle around selected frame */}
                      {navigationSettings.showFrameBoundariesAndLabels && (
                        <div
                          style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            width: `${Math.max(50, effectiveDisplayWidth * navigationSettings.stickyRatio)}px`,
                            height: `${Math.max(50, effectiveDisplayHeight * navigationSettings.stickyRatio)}px`,
                            transform: 'translate(-50%, -50%)',
                            border: '2px dashed #00FF00',
                            borderRadius: '50%',
                            pointerEvents: 'none',
                            zIndex: 2,
                          }}
                        />
                      )}

                      {navigationSettings.showFrameBoundariesAndLabels && (
                        <div style={{
                          position: 'absolute',
                          top: '10px',
                          left: '10px',
                          backgroundColor: 'rgba(0, 255, 0, 0.9)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          pointerEvents: 'none',
                          zIndex: 1,
                        }}>
                          <div>Frame {frameIndex} [SELECTED]</div>
                          <div style={{ fontSize: '10px', fontWeight: 'normal', marginTop: '2px', opacity: 0.9 }}>
                            {frameCamera.frame_file}
                          </div>
                        </div>
                      )}

                      {/* Marked item markers - persistent circles for items user marked for purchase */}
                      <FrameItemMarkers
                        frameIndex={frameIndex}
                        frameCamera={frameCamera}
                        frameDisplayWidth={effectiveDisplayWidth}
                        frameDisplayHeight={effectiveDisplayHeight}
                        shopId={shopId}
                        onNavigateToItem={(item) => {
                          const state = useAppStore.getState()
                          state.setManuallySelectedFrame(item.frameIndex)
                          state.centerViewportOnFrame(item.frameIndex)
                        }}
                      />

                      {/* Push-notification chat question marker — shows where the customer asked / is now */}
                      {pushChatMarker && (
                        <ChatQuestionMarker
                          questionFrameIndex={pushChatMarker.frameIndex}
                          imagePixelX={pushChatMarker.imagePixelX}
                          imagePixelY={pushChatMarker.imagePixelY}
                          point3D={pushChatMarker.point3D}
                          frameIndex={frameIndex}
                          frameCamera={frameCamera}
                          frameDisplayWidth={effectiveDisplayWidth}
                          frameDisplayHeight={effectiveDisplayHeight}
                          isLive={pushChatIsLive}
                        />
                      )}

                      {/* Tap Indicator - Red circle at projected 3D point */}
                      {/* PHASE 2: Block tap indicator during 3D data loading (Stage 2) */}
                      {canUseTapIndicator ? (
                        tapIndicator && tapIndicator.show && tapIndicator.frameIndex === frameIndex && tapIndicator.projections && (() => {
                          // For the SOURCE frame: always use original tap position (imagePixelX/Y)
                          // For OTHER frames (orbit navigation): use 3D-projected position from projections map
                          // Use tappedFrameIndex (immutable) NOT frameIndex (which changes during orbit)
                          const isSourceFrame = tapIndicator.tappedFrameIndex === frameIndex
                          const tapPixel: [number, number] = [tapIndicator.imagePixelX, tapIndicator.imagePixelY]
                          const projectedPixel = isSourceFrame ? tapPixel : tapIndicator.projections.get(frameIndex)
                          if (!projectedPixel) return null
                          const [displayX, displayY] = calculateIndicatorPosition(projectedPixel, frameCamera.intrinsics.width, frameCamera.intrinsics.height, effectiveDisplayWidth, effectiveDisplayHeight)
                          
                          // If user panned the indicator, use the panned position instead of recalculating
                          const indicatorPosition = tapIndicatorPannedPosition || { x: displayX, y: displayY }
                          
                          return (
                            <TapCameraIndicator
                              cameraCount={tapIndicator.cameraCount}
                              position={indicatorPosition}
                              currentDisplayX={displayX}
                              currentDisplayY={displayY}
                              frameRelativeTapX={tapIndicator.frameRelativeTapX}
                              frameRelativeTapY={tapIndicator.frameRelativeTapY}
                              imagePixelX={tapIndicator.imagePixelX}
                              imagePixelY={tapIndicator.imagePixelY}
                              frameCamera={frameCamera}
                              frameIndex={frameIndex}
                              cameras={cameras}
                              points3D={useAppStore.getState().points3D}
                              offsetMap={offsetMap}
                              frameDisplayWidth={effectiveDisplayWidth}
                              radiusPxImageSpace={tapIndicator.is2DOnly ? undefined : (discoveryState.radiusPx || tapIndicator.radiusPxImageSpace)}
                              scaleData={tapIndicator.is2DOnly ? undefined : tapIndicator.scaleData}
                              zoomScale={zoomScale}
                              onShowThumbnails={showThumbnails}
                              onUpdateRadius={tapIndicator.is2DOnly ? undefined : handleUpdateRadius}
                              onPanComplete={handleTapIndicatorPanComplete}
                              onCameraBadgeClick={tapIndicator.is2DOnly ? undefined : (() => setPlaybackTriggerCount(prev => prev + 1))}
                              onClose={() => setTapIndicator(null)}
                              shopId={shopId}
                              previewMode={tapIndicatorPreviewMode}
                              onActivate={() => setTapIndicatorPreviewMode(false)}
                              is2DOnly={tapIndicator.is2DOnly}
                              onMarkItem={() => {
                                handleMarkItem({
                                  imagePixelX: tapIndicator.imagePixelX,
                                  imagePixelY: tapIndicator.imagePixelY,
                                  radius: tapIndicator.is2DOnly ? 30 : (discoveryState.radiusPx || tapIndicator.radiusPxImageSpace || 30),
                                  point3D: tapIndicator.point3D,
                                  projections: tapIndicator.projections,
                                  frameIndex: tapIndicator.frameIndex,
                                })
                                setTapIndicator(null) // Dismiss tap indicator after marking
                              }}
                              onChatClick={() => setIsChatOpen(v => !v)}
                              onOrbitPrev={() => {
                                const store = useAppStore.getState()
                                const { orbitSequence, orbitIndex } = store
                                console.log(`◀ [OrbitBtn] pressed — orbitSequence=${orbitSequence?.length ?? 'null'} orbitIndex=${orbitIndex}`)
                                if (!orbitSequence || orbitSequence.length < 2) return
                                const prevIdx = (orbitIndex - 1 + orbitSequence.length) % orbitSequence.length
                                const entry = orbitSequence[prevIdx]
                                store.setOrbitIndex(prevIdx)
                                handleOrbitStep(entry.frameIndex, entry.projectedPixel)
                              }}
                              onOrbitNext={() => {
                                const store = useAppStore.getState()
                                const { orbitSequence, orbitIndex } = store
                                console.log(`▶ [OrbitBtn] pressed — orbitSequence=${orbitSequence?.length ?? 'null'} orbitIndex=${orbitIndex}`)
                                if (!orbitSequence || orbitSequence.length < 2) return
                                const nextIdx = (orbitIndex + 1) % orbitSequence.length
                                const entry = orbitSequence[nextIdx]
                                store.setOrbitIndex(nextIdx)
                                handleOrbitStep(entry.frameIndex, entry.projectedPixel)
                              }}
                            />
                          )
                        })()
                      ) : (
                        // Tap blocked message during Stage 2 (3D data loading)
                        <div className="tap-indicator-blocked">
                          <div className="tap-indicator-blocked__title">
                            <span className="tap-indicator-blocked__spinner" />
                            🔄 Loading 3D Data
                          </div>
                          <div className="tap-indicator-blocked__progress">
                            {loadingProgress}%
                          </div>
                          <div className="tap-indicator-blocked__stage">
                            {loadingStage}
                          </div>
                          <div className="tap-indicator-blocked__subtitle">
                            Tap will be enabled when ready
                          </div>
                        </div>
                      )}

                      {/* Shared Item Indicator - shows blue circle from WhatsApp URL while 3D data loads */}
                      {/* Once canUseTapIndicator is true, the programmatic tap useEffect replaces this with TapCameraIndicator */}
                      {sharedItemParams && sharedItemParams.targetFrame === frameIndex && (
                        <SharedItemIndicator 
                          urlParams={{ x: sharedItemParams.x, y: sharedItemParams.y, r: sharedItemParams.r }}
                          camera={frameCamera}
                          frameDisplayWidth={effectiveDisplayWidth}
                          frameDisplayHeight={effectiveDisplayHeight}
                        />
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* 🆕 ANIMATION FRAME LAYER - Shows during frame sequence animation */}
              {animatingFrameIndex !== null && animatingFrameIndex !== manuallySelectedFrameIndex && (() => {
                // 🔧 Use frameWorldPositions (unfiltered) instead of framesToRender (filtered by viewport)
                // Animation frames may be outside the contextual window but should still animate
                const animFramePos = frameWorldPositions.find(fp => fp.frameIndex === animatingFrameIndex)
                if (!animFramePos) return null
                
                const frameIndex = animatingFrameIndex
                const frameCamera = cameras[frameIndex]
                if (!frameCamera) return null

                // 🆕 Get animation config based on current trigger type
                const config = getAnimationConfig(animationTrigger)
                const zIndexValue = config?.zIndex ?? 40000
                const opacity = config?.opacity ?? 1.0
                const borderColor = config?.borderColor ?? '#FF00FF'
                const label = config?.label ?? 'ANIMATE'

                const displayCenterX = effectiveDisplayWidth / 2
                const displayCenterY = effectiveDisplayHeight / 2
                
                // 🎯 Position animation frame relative to currently selected frame
                // This centers the animation frames on the holy red dot
                const selectedFramePos = frameWorldPositions.find(fp => fp.frameIndex === manuallySelectedFrameIndex)
                
                let positionX = animFramePos.worldX - displayCenterX
                let positionY = animFramePos.worldY - displayCenterY
                
                // If we have a selected frame, position relative to it (for proper centering)
                if (selectedFramePos) {
                  const displacement = {
                    x: animFramePos.worldX - selectedFramePos.worldX,
                    y: animFramePos.worldY - selectedFramePos.worldY
                  }
                  positionX = selectedFramePos.worldX - displayCenterX + displacement.x
                  positionY = selectedFramePos.worldY - displayCenterY + displacement.y
                }

                return (
                  <div key={`frame-animating-${frameIndex}`}>
                    <div
                      style={{
                        position: 'absolute',
                        left: `${positionX}px`,
                        top: `${positionY}px`,
                        width: `${effectiveDisplayWidth}px`,
                        height: `${effectiveDisplayHeight}px`,
                        zIndex: zIndexValue,
                        opacity: opacity,
                        filter: 'none',
                        transition: 'none',
                        border: navigationSettings.showFrameBoundariesAndLabels ? `4px solid ${borderColor}` : 'none',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        imageRendering: 'auto'
                      }}
                    >
                      <img
                        src={getFrameImagePath(shopId, frameCamera.frame_file, currentSectionFramesPath || undefined)}
                        alt={`Frame ${frameIndex} (animating)`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        draggable={false}
                      />
                      {navigationSettings.showFrameBoundariesAndLabels && (
                        <div style={{
                          position: 'absolute',
                          top: '10px',
                          left: '10px',
                          backgroundColor: `rgba(${borderColor.includes('00FF') ? '0, 255' : '255, 0'}, 255, 0.9)`,
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          pointerEvents: 'none',
                          zIndex: 1,
                        }}>
                          Frame {frameIndex} [{label}]
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}

            </div>

            {/* Camera Trajectory Overlay - Shows consecutive camera centers path */}
            {/* Must be inside SimpleZoomPan to use same coordinate system as frames */}
            {/* enabled=true: Debug style (green circles, lines, numbers) */}
            {/* enabled=false: Production style (subtle blurred dots, no lines) */}
            <CameraTrajectoryOverlay
              enabled={navigationSettings.showFrameBoundariesAndLabels}
              lineColor="rgba(255, 255, 0, 1)"
              lineWidth={4}
              lineOpacity={1}
              onCircleClick={setHoveredCandidateFrameIndex}
              onClickedFrameChange={undefined}  // DISABLED: Yellow frame feature
              onBalloonSelect={setSelectedBalloonFrame}
              candidateFrameIndices={candidateFrameSet}
            />

          </SimpleZoomPan>

          {/* FIXED RED DOT AT CANVAS CENTER - Reference point for navigation */}
          {/* Positioned absolute within the canvas container (80vh viewport) */}
          {navigationSettings.showFrameBoundariesAndLabels && (
            <>
              {/* Red semi-transparent ellipse around red dot - same size as green dashed circle */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%) scale(' + zoomScale + ')',
                  width: `${Math.max(50, effectiveDisplayWidth * navigationSettings.stickyRatio)}px`,
                  height: `${Math.max(50, effectiveDisplayHeight * navigationSettings.stickyRatio)}px`,
                  border: '2px solid #ff00006c',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                  zIndex: 50,
                }}
              />
              {/* Red dot itself */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#FF0000',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                  zIndex: 999999,
                  boxShadow: '0 0 12px rgba(255, 0, 0, 0.9)',
                }}
              />
            </>
          )}

          {/* VIRTUAL FRAME - Frame size boundary for navigation calculations */}
          {/* 📦 Virtual frame positioned at world origin with actual frame dimensions */}
          {/* Frame 0 top-left at world (0, 0) - REMOVED: Debug overlay with dashed border */}
      </div>

      {/* BACK BUTTON - Moved to MobileControlsOverlay in GestureViewer.tsx */}
      {/* Hidden here to avoid duplicate button */}

      {/* CANDIDATE LIST SECTION - Bottom 20% - SHOW ALL FRAMES */}
      {showDebugOverlay && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100vw',
            height: '20vh',
            overflow: 'hidden',
            backgroundColor: '#1a1a1a',
            borderTop: '2px solid #333',
            padding: '12px',
            zIndex: 50,
          }}
        >
          <CandidateFrameList
            candidates={allCandidates}
            currentWinnerId={manuallySelectedFrameIndex ?? undefined}
            onBalloonSelect={setSelectedBalloonFrame}
            onBalloonHover={setHoveredBalloonFrame}
            selectedBalloonFrame={selectedBalloonFrame}
            onViewportHover={(frameIndex) => {
              // 🎬 CRITICAL: Suppress hover during animation to prevent z-index interference
              if (isFrameSequenceAnimating) {
                return // Don't update hover state during animation
              }
              setHoveredCandidateFrameIndex(frameIndex)
            }}
            onShowNavigationBalloon={() => setShowNavigationSettingsBalloon?.(true)}
            isPanningRef={isPanningRef}
            contextFrameCount={contextFrameIndices.size}
            viewportFrameCount={framesToRender.length}
          />
        </div>
      )}

      {/* Relationship balloon - rendered at top level to avoid parent overflow clipping */}
      {selectedBalloonFrame !== null && (
        <>
          {/* {console.log(`📢 [NewModeViewer] Opening balloon: from=${manuallySelectedFrameIndex} to=${hoveredBalloonFrame ?? selectedBalloonFrame} (selectedBalloon=${selectedBalloonFrame}, hoveredBalloon=${hoveredBalloonFrame})`)} */}
          <div data-balloon>
            <FrameRelationshipBalloon
              fromFrameIndex={manuallySelectedFrameIndex ?? 0}
              toFrameIndex={hoveredBalloonFrame ?? selectedBalloonFrame}
              offsetMap={offsetMap}
              cameras={cameras}
              onClose={() => {
                setSelectedBalloonFrame(null)
                setHoveredBalloonFrame(null)
              }}
            />
          </div>
        </>
      )}

      {/* Navigation Settings balloon - rendered at top level to avoid parent overflow clipping */}
      {showNavigationSettingsBalloon && (
        <div data-settings-panel>
          <NavigationSettingsBalloon
            onClose={() => setShowNavigationSettingsBalloon?.(false)}
          />
        </div>
      )}

      {/* 🧭 NAVIGATION HINTS - Rendered by separate NavigationHintOverlay component */}
      <NavigationHintOverlay />

      {/* 🗺️ MINIMAP - Toggle button (bottom-left) shows camera path overview */}
      <MinimapOverlay />

      {/* Camera Thumbnail List - Conditional overlay */}
      <CameraThumbnailList
        cameras={discoveryState.cameras}
        projections={discoveryState.projections || tapIndicator?.projections}
        circleRadiusPx={discoveryState.radiusPx}
        tappedFrameIndex={discoveryState.selectedFrameIndex}
        playbackTrigger={playbackTriggerCount}
        tapScreenY={tapIndicator?.screenPosition.y}
        onSelect={handleCameraSelection}
        onClose={() => {
          hideThumbnails()
          setTapIndicator(null)  // Clear data when closing thumbnails
          setTapIndicatorPannedPosition(null)  // Reset panned position too
        }}
        visible={discoveryState.visible}
        showDebugOverlay={showDebugOverlay}
        point3D={tapIndicator?.point3D}
        allCameras={cameras}
        onUpdateRadius={handleUpdateRadius}
      />

      {/* Home button removed — MobileControlsOverlay back button handles navigation */}

      {/* Delivery: Item list toggle button (top-right) */}
      <button
        className="item-list-toggle"
        onClick={() => setItemListOpen(prev => !prev)}
        style={{
          position: 'fixed', top: 12, right: 12, zIndex: 1999999,
          background: 'rgba(30,41,59,0.9)', backdropFilter: 'blur(10px)',
          border: '1px solid #334155', borderRadius: '50%',
          width: 44, height: 44, display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', color: '#f8fafc',
          boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 01-8 0" />
        </svg>
        {cartItemCount() > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700,
            borderRadius: 10, minWidth: 18, height: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
          }}>
            {cartItemCount()}
          </span>
        )}
      </button>

      {/* Delivery: ItemListPanel (slide-in from right) */}
      <ItemListPanel isOpen={itemListOpen} onClose={() => setItemListOpen(false)} onOpenCart={() => { setItemListOpen(false); setCartDrawerOpen(true) }} shopId={shopId} />

      {/* Wish list legend banner (color chips per person) */}
      <WishlistLegend shopId={shopId} />

      {/* Delivery: CartDrawer (bottom sheet) */}
      <CartDrawer isOpen={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)} />

      {/* Contextual chat balloon — appears when user taps the speech-bubble on a tap indicator */}
      {isChatOpen && tapIndicator && (() => {
        const containerRect = containerRef.current?.getBoundingClientRect()
        const anchorX = (containerRect?.left ?? 0) + (tapIndicatorPannedPosition?.x ?? tapIndicator.imagePixelX)
        const anchorY = (containerRect?.top ?? 0) + (tapIndicatorPannedPosition?.y ?? tapIndicator.imagePixelY)
        return (
          <ChatBalloon
            shopId={shopId}
            frameIndex={tapIndicator.frameIndex}
            imagePixelX={tapIndicator.imagePixelX}
            imagePixelY={tapIndicator.imagePixelY}
            point3D={tapIndicator.point3D}
            anchorX={anchorX}
            anchorY={anchorY}
            viewportWidth={window.innerWidth}
            viewportHeight={window.innerHeight}
            isOwner={isCurrentUserOwner}
            userId={chatUserId}
            onClose={() => setIsChatOpen(false)}
          />
        )
      })()}

      {/* Push notification deep-link: ChatBalloon opened for a specific thread (no tap indicator needed) */}
      {pushQuestionThreadId && (
        <ChatBalloon
          shopId={shopId}
          frameIndex={0}
          anchorX={window.innerWidth / 2}
          anchorY={window.innerHeight / 2}
          viewportWidth={window.innerWidth}
          viewportHeight={window.innerHeight}
          isOwner={true}
          userId={chatUserId}
          threadId={pushQuestionThreadId}
          onThreadLoaded={(threadData) => {
            // Navigate directly to the thread's frame (no animation) — same pattern as order item navigation
            const state = useAppStore.getState()
            if (threadData.frameIndex != null && threadData.frameIndex > 0) {
              state.setManuallySelectedFrame(threadData.frameIndex)
              state.centerViewportOnFrame(threadData.frameIndex)
            }
            // Show location marker on the frame where the question was asked
            setPushChatMarker({
              frameIndex: threadData.frameIndex ?? 0,
              imagePixelX: threadData.imagePixelX ?? undefined,
              imagePixelY: threadData.imagePixelY ?? undefined,
              point3D: threadData.point3D ?? null,
            })
          }}
          onLocationUpdate={(loc) => {
            // Live location from the customer — update marker position
            console.log('[NewModeViewer] onLocationUpdate', loc)
            setPushChatIsLive(true)
            setPushChatMarker({
              frameIndex: loc.frameIndex,
              imagePixelX: loc.imagePixelX,
              imagePixelY: loc.imagePixelY,
              point3D: loc.point3D ?? null,
            })
            // Auto-follow: navigate to the customer's current frame
            if (followCustomer) {
              const state = useAppStore.getState()
              state.setManuallySelectedFrame(loc.frameIndex)
              state.centerViewportOnFrame(loc.frameIndex)
            }
          }}
          onClose={() => {
            setPushQuestionThreadId(null)
            setPushChatMarker(null)
            setPushChatIsLive(false)
            setFollowCustomer(true)
          }}
        />
      )}

      {/* Follow/unfollow toggle for live customer tracking */}
      {pushQuestionThreadId && pushChatIsLive && (
        <button
          onClick={() => setFollowCustomer((f) => !f)}
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3000000,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 20,
            border: 'none',
            background: followCustomer ? '#16a34a' : 'rgba(30,41,59,0.85)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            backdropFilter: 'blur(8px)',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: followCustomer ? '#fff' : '#ef4444', display: 'inline-block' }} />
          {followCustomer ? 'Following customer' : 'Follow paused — tap to resume'}
        </button>
      )}

      {/* Long-press context menu (owner-only, rescan feature) */}
      {contextMenu?.open && (() => {
        const store = useAppStore.getState()
        const sectionId = store.activeUserSectionId
        const dataset = store.shopDatasets.find(d => d.sectionId === sectionId)
        const sectionName = (dataset as any)?.sectionName || ''
        return (
          <FrameContextMenu
            isOpen
            position={{ x: contextMenu.x, y: contextMenu.y }}
            frameIndex={contextMenu.frameIndex}
            frameFile={contextMenu.frameFile}
            sectionName={sectionName}
            onRescanArea={() => {
              const aid = sectionId || ''
              const sn = sectionName || ''
              const params = new URLSearchParams({
                shopId,
                sectionId: aid,
                sectionName: sn,
                mode: 'rescan',
                anchorFrameIndex: String(contextMenu.frameIndex),
                anchorFrameFile: contextMenu.frameFile,
              })
              setContextMenu(null)
              window.location.href = `/scan?${params.toString()}`
            }}
            onFrameInfo={() => {
              const cam = cameras[contextMenu.frameIndex]
              if (cam) {
                alert(
                  `Frame: ${cam.frame_file}\n` +
                  `Index: ${contextMenu.frameIndex}\n` +
                  `Position: [${cam.position.map(v => v.toFixed(4)).join(', ')}]\n` +
                  `Rotation: [${cam.rotation.map(v => v.toFixed(4)).join(', ')}]`
                )
              }
              setContextMenu(null)
            }}
            onClose={() => setContextMenu(null)}
          />
        )
      })()}
      
    </>
  )
}
